// Office.js wrapper — captures workbook snapshots for the server-side
// agent. Functions are guarded with `isInOffice()` so the same bundle
// runs in a plain browser tab (dev) without crashing.

import type { CellData, ColumnAggregate, NamedRange, SheetData, WorkbookContext } from '@/types/chat';
import { CHART_TILE, firstFreeTileIndex, tileAnchorCell, type Rect } from './chartLayout';
import { premiumChartDefaults } from './chartDefaults';
import { aggregationNumberFormat, friendlyHierarchyName } from './pivotDefaults';
import { nonNullColumnRuns } from './rangeWrite';
import { chartSourceLooksEmpty } from './chartSourceGuard';

// Workbook capture — balances token cost against the agent's ability
// to actually read data. The backend's read_range tool resolves against
// THIS snapshot, not live Excel; whatever we don't ship is unreadable
// until the model calls read_workbook explicitly.
//
// Three-tier capture strategy (Phase 8.3):
//   1. ACTIVE SHEET    — head sample (up to 30×30 populated cells) +
//                        ±2 neighborhood around the active cell. The
//                        sheet the user is actually working on gets
//                        full visibility for follow-ups.
//   2. NON-ACTIVE SHEETS — METADATA + HEADERS only. No cell preview.
//                        Saves ~500 tokens per turn for a 4-sheet
//                        workbook. Model sees "Sheet2 (50×4) — headers:
//                        Date, Region, Product, Qty" which is enough
//                        for routing decisions ("which sheet has sales?").
//                        For actual cell data the model calls
//                        read_workbook(read_range, sheet="Sheet2", ...).
//   3. NEIGHBORHOOD     — 5×5 around the active cell, merged into the
//                        active sheet's grid (not a separate slice).
//
// Pre-Phase 8.3 we shipped a 5-row preview for non-active sheets too —
// that turned out to be ~10K tokens of "just in case" data the model
// rarely consulted. Lazy-load via read_workbook is cheaper.
// MAX_COLS_PER_HEADER: how many columns of the first (header) row to capture
// per sheet. Wide sheets (500 cols) still get full header coverage so the
// AI can discover column letters for any named column via fuzzy_find and
// build size-agnostic formulas (=SUM(AE:AE)) without raw data reads.
const MAX_COLS_PER_HEADER = 200;            // headers only (first row, per sheet)
const ACTIVE_SHEET_HEAD_ROWS = 30;          // head sample for active sheet
const ACTIVE_SHEET_HEAD_COLS = 30;          // covers typical Excel layouts
// Column-aggregate pre-compute (Tier 2 — accurate aggregates on big sheets):
//   Only kicks in for the ACTIVE sheet when used range > AGGREGATE_THRESHOLD_ROWS.
//   Below that the snapshot already contains every cell, model can compute itself.
const AGGREGATE_THRESHOLD_ROWS = 50;
//   Per column we evaluate 9 metrics via the Excel.Functions API
//   (workbook.functions, ExcelApi 1.2) — Excel evaluates them WITHOUT
//   writing anything into the sheet, so capture never dirties the
//   workbook, never wipes the user's Ctrl+Z stack, and never fires
//   worksheets.onChanged (which would self-defeat the snapshot cache).
//   Raised to 30 cols (was 15) so wide sheets get aggregate coverage for
//   more numeric columns — AI sees SUM/AVG/MIN/MAX for cols up to col 30
//   even when it can't see the raw cell data.
//   30 cols × 9 metrics = 270 FunctionResults resolved in ONE sync.
const MAX_AGGREGATE_COLUMNS = 30;
//   Sample rows used to decide if a column is numeric. 60% threshold —
//   tolerates one occasional non-numeric cell (footer label, NA marker)
//   without false-negative on legitimate numeric columns.
const AGGREGATE_DETECT_SAMPLE = 5;
const AGGREGATE_DETECT_THRESHOLD = 0.6;
const ACTIVE_SHEET_CELL_CAP = 600;          // hard ceiling, populated cells only
const ACTIVE_NEIGHBORHOOD_RADIUS = 2;       // ±2 cells around active = 5×5 grid
// Structural metadata caps — Excel Tables (ListObjects) + merged areas
// per sheet. Both are cheap address strings; the caps just bound the
// snapshot payload on pathological workbooks (100s of tables/merges).
const MAX_TABLES_PER_SHEET = 10;
const MAX_MERGED_AREAS_PER_SHEET = 10;
const ACTIVE_SHEET_TAIL_ROWS = 5;           // tail sample size when sheet is large
const TAIL_THRESHOLD_ROWS = 50;             // rows beyond head sample needed before we capture a tail too

/** Convert column letter (A..Z, AA..ZZ) to 1-indexed column number. */
function colLetterToIndex(letter: string): number {
  let n = 0;
  for (const ch of letter) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n;
}

/** Parse an A1 range address ("A1:E100" or single "A1") into 1-indexed bounds. */
function parseRangeAddress(addr: string): {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
} | null {
  if (!addr) return null;
  const cleaned = addr.replace(/\$/g, '');
  const m = cleaned.match(/^([A-Z]+)(\d+)(?::([A-Z]+)(\d+))?$/);
  if (!m) return null;
  const startCol = colLetterToIndex(m[1]);
  const startRow = parseInt(m[2], 10);
  const endCol = m[3] ? colLetterToIndex(m[3]) : startCol;
  const endRow = m[4] ? parseInt(m[4], 10) : startRow;
  return { startCol, startRow, endCol, endRow };
}

/**
 * Decide the capture window for a sheet given its used range.
 * - Anchors header + slice to where data actually starts (handles padding rows / non-A1 layouts).
 * - For large sheets, also captures a small tail so the model sees beginning + end.
 * Returns null when the sheet is empty.
 */
type CaptureWindow = {
  startRow: number;        // 1-indexed: first row of data (used range top)
  startCol: number;        // 1-indexed: first col of data (used range left)
  headerEndCol: number;    // 1-indexed: last col for the header row (capped)
  sliceStartRow: number;   // 1-indexed: head sample top row (= startRow)
  sliceEndRow: number;     // 1-indexed: head sample bottom row
  sliceEndCol: number;     // 1-indexed: head sample right col (capped)
  tailStartRow?: number;   // 1-indexed: tail sample top row, undefined if no tail
  tailEndRow?: number;     // 1-indexed: tail sample bottom row
  totalRows: number;       // used.rowCount (real)
  totalCols: number;       // used.columnCount (real)
};

function computeCaptureWindow(
  usedAddr: string | null,
  usedRowCount: number,
  usedColCount: number,
): CaptureWindow | null {
  const parsed = parseRangeAddress(usedAddr ?? '');
  if (!parsed) return null;
  const totalRows = usedRowCount > 0 ? usedRowCount : parsed.endRow - parsed.startRow + 1;
  const totalCols = usedColCount > 0 ? usedColCount : parsed.endCol - parsed.startCol + 1;
  const headerEndCol = Math.min(parsed.endCol, parsed.startCol + MAX_COLS_PER_HEADER - 1);
  const sliceEndCol = Math.min(parsed.endCol, parsed.startCol + ACTIVE_SHEET_HEAD_COLS - 1);
  const sliceEndRow = Math.min(parsed.endRow, parsed.startRow + ACTIVE_SHEET_HEAD_ROWS - 1);

  let tailStartRow: number | undefined;
  let tailEndRow: number | undefined;
  // Capture a tail sample only when there's enough data after the head
  // to make it meaningful — otherwise head already covers the sheet.
  if (parsed.endRow > sliceEndRow + TAIL_THRESHOLD_ROWS) {
    tailEndRow = parsed.endRow;
    tailStartRow = parsed.endRow - ACTIVE_SHEET_TAIL_ROWS + 1;
  }

  return {
    startRow: parsed.startRow,
    startCol: parsed.startCol,
    headerEndCol,
    sliceStartRow: parsed.startRow,
    sliceEndRow,
    sliceEndCol,
    tailStartRow,
    tailEndRow,
    totalRows,
    totalCols,
  };
}

export function isInOffice(): boolean {
  return typeof Office !== 'undefined' && !!Office?.context?.host;
}

// delay resolves after ms — used to pace the visual apply phase so charts
// render one-by-one (Office only paints on ctx.sync, so the pause runs
// AFTER each sync resolves).
const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function officeReady(): Promise<void> {
  return new Promise((resolve) => {
    if (!isInOffice()) {
      resolve();
      return;
    }
    Office.onReady(() => {
      // Wire snapshot-cache invalidation to real workbook activity. Until
      // now the cache only dropped on the 250ms selection poll, so an
      // in-place edit that didn't move the selection (paste-in-place,
      // ribbon op, fill) could serve stale cells for up to the TTL. These
      // handlers make the "any user activity drops the memo" claim true.
      void registerSnapshotInvalidation();
      resolve();
    });
  });
}

// registerSnapshotInvalidation is idempotent — guarded so repeated
// officeReady() calls don't stack duplicate handlers.
let snapshotInvalidationWired = false;

async function registerSnapshotInvalidation(): Promise<void> {
  if (snapshotInvalidationWired || !isInOffice() || typeof Excel === 'undefined') return;
  snapshotInvalidationWired = true;
  try {
    await Excel.run(async (ctx) => {
      // Cell edits anywhere in the workbook.
      ctx.workbook.worksheets.onChanged.add(async () => {
        invalidateSnapshotCache();
      });
      // Selection moves (also covered by the poll, but this is cheaper
      // and event-driven).
      ctx.workbook.onSelectionChanged.add(async () => {
        invalidateSnapshotCache();
      });
      // Structural changes: sheet add/delete/rename.
      ctx.workbook.worksheets.onAdded.add(async () => {
        invalidateSnapshotCache();
      });
      ctx.workbook.worksheets.onDeleted.add(async () => {
        invalidateSnapshotCache();
      });
      await ctx.sync();
    });
  } catch {
    // Older hosts may not support every event; degrade to poll-only
    // invalidation rather than crashing init.
    snapshotInvalidationWired = false;
  }
}

/** Detects Office.js errors that reflect a USER-STATE block — not a
 *  bug in our code. Two common ones Excel emits:
 *   (a) "Excel is in cell-editing mode" — user is typing inside a cell,
 *       cursor is blinking; every Excel.run fails until ENTER / TAB /
 *       Escape exits edit mode.
 *   (b) "The current selection is invalid for this operation" — user
 *       has a chart, shape, image, or pivot selected (not a cell range),
 *       so APIs that target cells (getActiveCell, getSelectedRange,
 *       read_range, set_range) fail.
 *  Both are recoverable by the user clicking a cell; we just need to
 *  tell them what to do instead of surfacing the raw RichApi stack. */
export function isCellEditingModeError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return msg.includes('cell-editing mode') ||
    msg.includes('selection is invalid') ||
    msg.includes('invalid selection');
}

/** True ONLY for the "user is typing inside a cell" case. Cannot be
 *  recovered programmatically — the user must press Enter/Escape/Tab. */
export function isCellTypingMode(err: unknown): boolean {
  if (!err) return false;
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return msg.includes('cell-editing mode');
}

/** True for the "chart/shape/image is selected" case. Recoverable by
 *  programmatically selecting a cell — see recoverFromInvalidSelection. */
export function isInvalidSelectionError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return msg.includes('selection is invalid') || msg.includes('invalid selection');
}

/** Programmatically deselects any chart/object by selecting cell A1 on
 *  the active sheet. Best-effort — silently swallows failures so the
 *  caller can decide whether to retry the original op. Returns true when
 *  the recovery select succeeded; false otherwise. */
export async function recoverFromInvalidSelection(): Promise<boolean> {
  if (!isInOffice() || typeof Excel === 'undefined') return false;
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.getRange('A1').select();
      await ctx.sync();
    });
    return true;
  } catch {
    return false;
  }
}

// --- Live range reads (client-tool channel) --------------------------------

// Cap on cells returned by one live read. The backend independently
// rejects requests over its own (higher) bound; this cap keeps the
// result POST body and Office.js load times sane. When we clip, the
// result carries truncated=true so the model knows to narrow the range.
export const MAX_LIVE_READ_CELLS = 20000;

export interface LiveRangeReadResult {
  sheet: string;
  range: string;
  origin: string;
  row_count: number;
  col_count: number;
  values: unknown[][];
  formulas: string[][];
  truncated: boolean;
}

/**
 * Reads values + formulas for an A1 range from live Excel. Used by the
 * client-tool channel when the backend agent needs cells beyond the
 * snapshot. Throws on Office.js errors — the caller converts those into
 * an {ok:false} result for the server.
 */
export async function readRangeLive(
  sheetName: string | undefined,
  rangeA1: string,
): Promise<LiveRangeReadResult> {
  if (!isInOffice() || typeof Excel === 'undefined') {
    throw new Error('not running inside Excel');
  }
  return await Excel.run(async (ctx) => {
    const sheet = sheetName
      ? ctx.workbook.worksheets.getItem(sheetName)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    let range = sheet.getRange(rangeA1);
    range.load(['rowCount', 'columnCount']);
    await ctx.sync();

    let truncated = false;
    const totalCells = range.rowCount * range.columnCount;
    if (totalCells > MAX_LIVE_READ_CELLS) {
      // First clamp columns if a single row already blows the cap (very
      // wide range), THEN clip rows to fit — so the returned grid never
      // exceeds MAX_LIVE_READ_CELLS even on pathological requests.
      let keepCols = range.columnCount;
      if (keepCols > MAX_LIVE_READ_CELLS) {
        keepCols = MAX_LIVE_READ_CELLS;
        range = range.getResizedRange(0, keepCols - range.columnCount);
      }
      const keepRows = Math.max(1, Math.floor(MAX_LIVE_READ_CELLS / keepCols));
      range = range.getResizedRange(keepRows - range.rowCount, 0);
      truncated = true;
    }
    range.load(['address', 'rowCount', 'columnCount', 'values', 'formulas']);
    await ctx.sync();

    const addr = range.address.includes('!')
      ? range.address.split('!')[1]
      : range.address;
    const cleanAddr = addr.replace(/\$/g, '');
    const origin = cleanAddr.split(':')[0];
    // Office.js echoes literal values in `formulas` for non-formula
    // cells — normalize so only real "="-prefixed formulas survive.
    const formulas = (range.formulas as unknown[][]).map((row) =>
      row.map((f) => (typeof f === 'string' && f.startsWith('=') ? f : '')),
    );

    return {
      sheet: sheet.name,
      range: cleanAddr,
      origin,
      row_count: range.rowCount,
      col_count: range.columnCount,
      values: range.values as unknown[][],
      formulas,
      truncated,
    };
  });
}

/** Selects a sheet + range in the live workbook and activates the sheet,
 *  so a user clicking a cell citation ([Sheet1!B15]) jumps straight to it.
 *  Best-effort — swallows errors (e.g. sheet/range no longer exists). */
export async function selectRange(sheetName: string, rangeA1: string): Promise<void> {
  if (!isInOffice() || typeof Excel === 'undefined') return;
  try {
    await Excel.run(async (ctx) => {
      const sheet = sheetName
        ? ctx.workbook.worksheets.getItem(sheetName)
        : ctx.workbook.worksheets.getActiveWorksheet();
      sheet.activate();
      sheet.getRange(rangeA1).select();
      await ctx.sync();
    });
  } catch {
    // Silent — a stale citation shouldn't throw a UI error.
  }
}

/** Lightweight snapshot: just active sheet + active cell + selection.
 *  Polled on SelectionChanged at UI refresh rate. */
export async function captureActiveContext(): Promise<WorkbookContext> {
  if (!isInOffice() || typeof Excel === 'undefined') {
    return {};
  }
  try {
    return await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const activeCell = ctx.workbook.getActiveCell();
      activeCell.load('address');
      const selection = ctx.workbook.getSelectedRange();
      selection.load('address');
      await ctx.sync();

      return {
        active_sheet: sheet.name,
        active_cell: stripSheetPrefix(activeCell.address),
        selected_range: stripSheetPrefix(selection.address),
      };
    });
  } catch (err) {
    // This is the 250ms SelectionChanged poll — it must NEVER throw.
    // Selecting a chart / shape / image / pivot makes getActiveCell &
    // getSelectedRange raise RichApi "selection is invalid for this
    // operation". That is a normal, common state — especially around
    // dashboards full of charts — not an error to surface. Re-throwing it
    // here produced an uncaught promise rejection every tick. Return empty
    // context; the next tick recovers the moment the user clicks a cell.
    // (The chat SEND path uses captureWorkbookSnapshotCached, which has its
    // own invalid-selection recovery, so it is unaffected by this change.)
    if (Math.random() < 0.02) {
      console.warn('[office] captureActiveContext poll skipped:', err);
    }
    return {};
  }
}

/** Full snapshot: sheets + headers + grid + named ranges.
 *  Call once per chat turn, not on every selection change.
 *
 *  Office.js load/sync gotcha: `sheets.load('items/name')` then reading
 *  `s.name` inside a loop throws "property not available" on recent
 *  Excel builds. We do multi-pass loads with explicit per-object
 *  `.load()` + `.sync()` between passes. Errors log loudly — silent
 *  empty snapshots were masking real bugs.
 */
// captureWorkbookSnapshot captures LAZY metadata — no cell grids.
// The agent fetches actual values on demand via the read_workbook tool.
// Only ~5×5 around the active cell is included for short-range context
// (questions like "what's in this cell?" should resolve without a
// tool roundtrip).
export async function captureWorkbookSnapshot(): Promise<WorkbookContext> {
  if (!isInOffice() || typeof Excel === 'undefined') return {};
  try {
    return await Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      const sheets = wb.worksheets;
      const activeSheet = wb.worksheets.getActiveWorksheet();
      const activeCell = wb.getActiveCell();
      const selection = wb.getSelectedRange();
      const names = wb.names;

      // Pass 1: collection + scalar properties.
      sheets.load('items');
      activeSheet.load('name');
      activeCell.load(['address', 'rowIndex', 'columnIndex']);
      selection.load('address');
      names.load('items/name,items/value,items/scope');
      await ctx.sync();

      // Pass 2: explicit per-sheet load so .name is readable.
      for (const s of sheets.items) {
        s.load('name');
      }
      await ctx.sync();

      const out: WorkbookContext = {
        active_sheet: activeSheet.name,
        active_cell: stripSheetPrefix(activeCell.address),
        selected_range: stripSheetPrefix(selection.address),
        sheets: [],
        named_ranges: [],
      };

      // Pass 3a (metadata batch): for every sheet, queue used-range +
      // chart/pivot loads. Single sync collects all of them so we know
      // WHERE data is on each sheet before deciding what to fetch in
      // the adaptive pass below.
      //
      // Why two passes:
      //   - Old code hard-coded A1:Z30 capture irrespective of where
      //     the data actually lived on the sheet. Sheets with padding
      //     rows / data starting at B100 came back EMPTY because we
      //     read the wrong cells.
      //   - Now we first learn the used range, then anchor capture to
      //     wherever the data really starts (head + tail for big sheets).
      type SheetMeta = {
        s: Excel.Worksheet;
        sheetName: string;
        isActive: boolean;
        used: Excel.Range;
        charts: Excel.ChartCollection;
        pivots: Excel.PivotTableCollection;
        tables: Excel.TableCollection;
      };
      const sheetMetas: SheetMeta[] = [];
      for (const s of sheets.items) {
        const used = s.getUsedRangeOrNullObject(false);
        used.load(['address', 'rowCount', 'columnCount', 'isNullObject']);
        const charts = s.charts;
        const pivots = s.pivotTables;
        const tables = s.tables;
        charts.load('items/name,items/title/text');
        pivots.load('items/name');
        tables.load('items/name');
        sheetMetas.push({
          s,
          sheetName: s.name,
          isActive: s.name === activeSheet.name,
          used,
          charts,
          pivots,
          tables,
        });
      }
      await ctx.sync();

      // Pass 3b (adaptive batch): now that we know each sheet's used
      // range, queue header / head-slice / tail-slice / pivot-range
      // loads anchored to the real data location. One more sync to
      // pull the actual values.
      type SheetCapture = {
        meta: SheetMeta;
        sheet: SheetData;
        window: CaptureWindow | null;
        headerRange: Excel.Range | null;
        sliceRange: Excel.Range | null;
        tailRange: Excel.Range | null;
        pivotRangeRefs: Array<{ name: string; range: Excel.Range }>;
        tableRangeRefs: Array<{ name: string; range: Excel.Range }>;
        mergedAreas: Excel.RangeAreas | null;
      };
      const captures: SheetCapture[] = [];
      for (const meta of sheetMetas) {
        const usedAddr = meta.used.isNullObject ? null : stripSheetPrefix(meta.used.address);
        const window = computeCaptureWindow(
          usedAddr,
          meta.used.isNullObject ? 0 : meta.used.rowCount,
          meta.used.isNullObject ? 0 : meta.used.columnCount,
        );

        const sheet: SheetData = {
          name: meta.sheetName,
          used_range: usedAddr ?? undefined,
          row_count: meta.used.isNullObject ? 0 : meta.used.rowCount,
          col_count: meta.used.isNullObject ? 0 : meta.used.columnCount,
        };

        let headerRange: Excel.Range | null = null;
        let sliceRange: Excel.Range | null = null;
        let tailRange: Excel.Range | null = null;

        if (window) {
          // Anchor the header read to the FIRST row of the used range —
          // not row 1. Sheets with padding/title rows up top are
          // common; old code returned empty headers for them.
          const headerAddr = `${columnLetter(window.startCol)}${window.startRow}:${columnLetter(window.headerEndCol)}${window.startRow}`;
          headerRange = meta.s.getRange(headerAddr);
          headerRange.load('values');

          if (meta.isActive) {
            const sliceAddr = `${columnLetter(window.startCol)}${window.sliceStartRow}:${columnLetter(window.sliceEndCol)}${window.sliceEndRow}`;
            sliceRange = meta.s.getRange(sliceAddr);
            sliceRange.load(['values', 'formulas', 'valueTypes']);

            // Tail sample for large sheets — model sees BOTH the start
            // and end of the data so it can detect sort order, gaps,
            // freshness without a full read. Cheap (~5 rows extra).
            if (window.tailStartRow && window.tailEndRow) {
              const tailAddr = `${columnLetter(window.startCol)}${window.tailStartRow}:${columnLetter(window.sliceEndCol)}${window.tailEndRow}`;
              tailRange = meta.s.getRange(tailAddr);
              tailRange.load(['values', 'formulas', 'valueTypes']);
            }
          }
        }

        // Pivot range refs — same as before, queued here so they sync
        // in the same batch as the cell loads.
        const pivotRangeRefs: Array<{ name: string; range: Excel.Range }> = [];
        if (meta.pivots.items && meta.pivots.items.length > 0) {
          for (const p of meta.pivots.items) {
            const r = p.layout.getRange();
            r.load('address');
            pivotRangeRefs.push({ name: p.name?.trim() ?? '', range: r });
          }
        }

        // Excel Table (ListObject) ranges — name + address so the model
        // can use structured refs and avoid writing inside tables.
        // ExcelApi 1.1; capped per sheet to bound payload size.
        const tableRangeRefs: Array<{ name: string; range: Excel.Range }> = [];
        if (meta.tables.items && meta.tables.items.length > 0) {
          for (const t of meta.tables.items.slice(0, MAX_TABLES_PER_SHEET)) {
            const r = t.getRange();
            r.load('address');
            tableRangeRefs.push({ name: t.name?.trim() ?? '', range: r });
          }
        }

        // Merged areas inside the used range. getMergedAreasOrNullObject
        // is ExcelApi 1.13 — gate on the requirement set AND try/catch so
        // older hosts silently omit the field instead of poisoning the
        // shared batch sync below.
        let mergedAreas: Excel.RangeAreas | null = null;
        if (!meta.used.isNullObject
          && Office.context.requirements.isSetSupported('ExcelApi', '1.13')) {
          try {
            mergedAreas = meta.used.getMergedAreasOrNullObject();
            mergedAreas.load(['address', 'isNullObject']);
          } catch {
            mergedAreas = null;
          }
        }

        captures.push({
          meta, sheet, window, headerRange, sliceRange, tailRange,
          pivotRangeRefs, tableRangeRefs, mergedAreas,
        });
      }
      await ctx.sync();

      // Pass 3c (build): assemble SheetData from the loaded values.
      for (const cap of captures) {
        const {
          sheet, window, headerRange, sliceRange, tailRange,
          pivotRangeRefs, tableRangeRefs, mergedAreas, meta,
        } = cap;

        // Headers from the anchored header row.
        if (headerRange && headerRange.values && headerRange.values[0]) {
          const raw = headerRange.values[0] as unknown[];
          sheet.headers = raw
            .map((v) => (v == null ? '' : String(v)))
            .filter((v, i, arr) => {
              // Drop trailing empty header cells but keep interior empties.
              if (v.trim() !== '') return true;
              return arr.slice(i + 1).some((x) => x != null && String(x).trim() !== '');
            });
        }

        // Charts: prefer title text, fall back to internal name.
        if (meta.charts.items && meta.charts.items.length > 0) {
          const names = meta.charts.items
            .map((c) => {
              const t = c.title?.text?.trim();
              if (t) return t;
              return c.name?.trim() ?? '';
            })
            .filter((n) => n !== '');
          if (names.length > 0) sheet.charts = names;
        }

        // Pivots with their ranges (helps model avoid writing inside).
        if (pivotRangeRefs.length > 0) {
          try {
            const labels: string[] = [];
            for (const ref of pivotRangeRefs) {
              if (!ref.name) continue;
              const addr = stripSheetPrefix(ref.range.address ?? '');
              labels.push(addr ? `${ref.name} (${addr})` : ref.name);
            }
            if (labels.length > 0) sheet.pivots = labels;
          } catch {
            const names = pivotRangeRefs.map((r) => r.name).filter((n) => n !== '');
            if (names.length > 0) sheet.pivots = names;
          }
        }

        // Excel Tables with their ranges (same rationale as pivots).
        if (tableRangeRefs.length > 0) {
          try {
            const tbls: Array<{ name: string; range: string }> = [];
            for (const ref of tableRangeRefs) {
              if (!ref.name) continue;
              const addr = stripSheetPrefix(ref.range.address ?? '');
              if (addr) tbls.push({ name: ref.name, range: addr });
            }
            if (tbls.length > 0) sheet.tables = tbls;
          } catch {
            // Best-effort — a table whose range failed to load shouldn't
            // fail the snapshot.
          }
        }

        // Merged areas. RangeAreas.address is a comma-separated list of
        // sheet-prefixed A1 ranges ("Sheet1!B2:D2, Sheet1!A5:A8").
        if (mergedAreas) {
          try {
            if (!mergedAreas.isNullObject && mergedAreas.address) {
              const areas = mergedAreas.address
                .split(',')
                .map((a) => stripSheetPrefix(a.trim()))
                .filter((a) => a !== '')
                .slice(0, MAX_MERGED_AREAS_PER_SHEET);
              if (areas.length > 0) sheet.merged_ranges = areas;
            }
          } catch {
            // Unsupported host / transient load failure — silently omit.
          }
        }

        // Build the cell grid (active sheet only). Cells are 0-indexed
        // in the array, but each cell carries its ABSOLUTE Excel
        // address. head_origin tells the backend where to anchor
        // cells[0][0] — defaults to {1,1} for backward compat.
        if (sliceRange && window) {
          sheet.head_origin = { row: window.sliceStartRow, col: window.startCol };
          sheet.cells = buildGrid(sliceRange, window.startCol, window.sliceStartRow);
        }
        // Tail rows: stored separately so the backend prose can render
        // them with a "..." marker between head and tail. Each cell
        // carries its absolute address so model can reference correctly.
        if (tailRange && window && window.tailStartRow) {
          sheet.tail_origin = { row: window.tailStartRow, col: window.startCol };
          sheet.tail_cells = buildGrid(tailRange, window.startCol, window.tailStartRow);
        }

        out.sheets!.push(sheet);
      }

      // Pass 4: ±2 neighborhood around the active cell so "what's in
      // this cell?" questions resolve without a read_range round-trip.
      // MERGED into the active-sheet grid (earlier builds overwrote
      // the head sample here — that was a silent bug).
      if (activeCell && !activeCell.isNullObject) {
        const r = activeCell.rowIndex;
        const c = activeCell.columnIndex;
        const r0 = Math.max(0, r - ACTIVE_NEIGHBORHOOD_RADIUS);
        const c0 = Math.max(0, c - ACTIVE_NEIGHBORHOOD_RADIUS);
        const r1 = r + ACTIVE_NEIGHBORHOOD_RADIUS;
        const c1 = c + ACTIVE_NEIGHBORHOOD_RADIUS;
        const addr = `${columnLetter(c0 + 1)}${r0 + 1}:${columnLetter(c1 + 1)}${r1 + 1}`;
        try {
          const nbrRange = activeSheet.getRange(addr);
          nbrRange.load(['values', 'formulas', 'valueTypes']);
          await ctx.sync();
          const values = nbrRange.values ?? [];
          const formulas = nbrRange.formulas ?? [];
          const types = nbrRange.valueTypes ?? [];

          const activeSheetRec = out.sheets!.find((sh) => sh.name === activeSheet.name);
          if (!activeSheetRec) {
            return out;
          }
          // Neighborhood overlay assumes cells[0][0] === A1. With
          // adaptive head_origin (data starts elsewhere), the array
          // indexing convention shifts and overlaying here would write
          // to the wrong slot. Skip — the model can fetch via
          // read_range if it really needs that neighborhood.
          const origin = activeSheetRec.head_origin ?? { row: 1, col: 1 };
          if (origin.row !== 1 || origin.col !== 1) {
            return out;
          }
          // Ensure the grid is at least big enough to hold the
          // neighborhood rows. This only extends beyond the head
          // sample for active cells BELOW row HEAD_ROWS.
          activeSheetRec.cells ??= [];
          for (let rr = 0; rr < values.length; rr++) {
            const rowIdx = r0 + rr;
            while (activeSheetRec.cells.length <= rowIdx) {
              activeSheetRec.cells.push([]);
            }
            const targetRow = activeSheetRec.cells[rowIdx];
            for (let cc = 0; cc < (values[rr]?.length ?? 0); cc++) {
              const colIdx = c0 + cc;
              const cellAddr = `${columnLetter(colIdx + 1)}${rowIdx + 1}`;
              const v = values[rr]?.[cc];
              const f = formulas[rr]?.[cc];
              const t = mapValueType(types[rr]?.[cc]);
              // Pad row with empty placeholders up to this column so
              // the 2D index [colIdx] stays correct for read_range.
              while (targetRow.length <= colIdx) {
                const padAddr = `${columnLetter(targetRow.length + 1)}${rowIdx + 1}`;
                targetRow.push({ address: padAddr });
              }
              targetRow[colIdx] = (v === '' && (!f || f === ''))
                ? { address: cellAddr }
                : {
                    address: cellAddr,
                    value: v as CellData['value'],
                    formula: typeof f === 'string' && f.startsWith('=') ? f : undefined,
                    type: t,
                  };
            }
          }
        } catch {
          // Neighborhood is a nice-to-have; never fail the whole
          // snapshot just because an edge range couldn't load.
        }
      }

      // Pass 5: column aggregates (Tier 2 — accurate sum/count/mean/min/max
      // per numeric column via the Excel.Functions API — no sheet writes).
      // Computed for EVERY data sheet above the threshold, not just the
      // active one: a dashboard build writes KPIs against a SOURCE sheet
      // (e.g. 'Employee Master') that is NOT active, and needs exact
      // aggregates for those columns to avoid guessing magnitudes. Capped
      // to a few sheets to bound snapshot latency. Best-effort per sheet.
      const AGGREGATE_MAX_SHEETS = 4;
      let aggSheetsDone = 0;
      for (const rec of out.sheets ?? []) {
        if (aggSheetsDone >= AGGREGATE_MAX_SHEETS) break;
        if ((rec.row_count ?? 0) <= AGGREGATE_THRESHOLD_ROWS) continue;
        try {
          const numericCols = detectNumericColumns(rec);
          if (numericCols.length === 0) continue;
          // Anchor the aggregate window to the sheet's REAL used range.
          // The old math passed head_origin.row + row_count, treating the
          // row COUNT as an absolute end row — correct only when the used
          // range starts at row 1. For a used range like A5:K200 (title
          // rows up top / data pasted lower) the SUM/AVG/COUNT ranges
          // silently covered the wrong rows. used_range was captured in
          // pass 3a, so parse it for the absolute first/last data rows.
          const usedBounds = parseRangeAddress(rec.used_range ?? '');
          if (!usedBounds) continue;
          const ws = rec.name === activeSheet.name
            ? activeSheet
            : ctx.workbook.worksheets.getItem(rec.name);
          const aggregates = await computeColumnAggregates(
            ctx,
            ws,
            numericCols.slice(0, MAX_AGGREGATE_COLUMNS),
            usedBounds.startRow,
            usedBounds.endRow,
          );
          if (aggregates.length > 0) {
            rec.column_aggregates = aggregates;
          }
          aggSheetsDone++;
        } catch (err) {
          // Protected sheet / transient Office.js error must not fail the
          // whole snapshot — skip aggregates for this sheet and continue.
          console.warn('[office] column aggregate pre-compute skipped for', rec.name, err);
        }
      }

      if (names?.items) {
        for (const n of names.items) {
          out.named_ranges!.push({
            name: n.name,
            scope: typeof n.scope === 'string' ? n.scope : undefined,
            range: typeof n.value === 'string' ? n.value : String(n.value ?? ''),
          } as NamedRange);
        }
      }
      return out;
    });
  } catch (err) {
    console.error('[office] captureWorkbookSnapshot failed:', err);
    // User-state errors (cell-edit mode, chart/object selected) are
    // RECOVERABLE by user action — re-throw so callers can show an
    // actionable toast instead of silently submitting an empty snapshot
    // and confusing the backend agent.
    if (isCellEditingModeError(err)) {
      throw err;
    }
    return {};
  }
}

// Phase 7 — Snapshot cache (UX latency win on rapid follow-ups).
//
// captureWorkbookSnapshot() walks every sheet via Excel.run() — typically
// 100-300ms per call. When the user fires multiple chat turns in quick
// succession (e.g., "ab bold kar", "title bada karo"), recomputing the
// snapshot every time is wasteful. We memo the last successful capture
// for SNAPSHOT_CACHE_TTL_MS and verify the workbook hash hasn't changed
// before serving the cached value.
//
// Token impact: zero — backend gets identical bytes either way, and
// Anthropic/Gemini auto-cache identical bytes. This is purely a frontend
// latency + CPU win.
//
// Safety:
//   - Tight 5-second TTL: even if a user edits cells off-screen between
//     turns, they almost always either pause first or trigger
//     SelectionChanged (which invalidates).
//   - computeWorkbookHash() is called every time and compared with the
//     cached hash — so structural changes (sheet add/delete/rename)
//     immediately bypass the cache.
//   - Worksheets.onChanged + onSelectionChanged + onAdded/onDeleted are
//     registered at init (registerSnapshotInvalidation) and all call
//     invalidateSnapshotCache(), so any user activity — including an
//     in-place edit that doesn't move the selection — drops the memo.
const SNAPSHOT_CACHE_TTL_MS = 5_000;

let snapshotCache: { snapshot: WorkbookContext; hash: string; at: number } | null = null;

/** Drop the cached snapshot — call when ANY user activity could have
 *  changed the workbook (selection move, cell edit, sheet add). */
export function invalidateSnapshotCache(): void {
  snapshotCache = null;
}

/** Capture-with-memo: returns the cached snapshot when fresh (< TTL)
 *  and structurally identical (same hash). Otherwise re-captures fresh.
 *
 *  Returns the SAME shape as a manual captureWorkbookSnapshot + hash
 *  pair so callers can drop in this helper.
 */
export async function captureWorkbookSnapshotCached(): Promise<{
  snapshot: WorkbookContext;
  hash: string;
}> {
  const now = Date.now();

  // Fast path: cache fresh AND structurally unchanged → reuse memo.
  if (snapshotCache && now - snapshotCache.at < SNAPSHOT_CACHE_TTL_MS) {
    const currentHash = await computeWorkbookHash();
    if (currentHash === snapshotCache.hash) {
      return { snapshot: snapshotCache.snapshot, hash: currentHash };
    }
    // Hash changed — invalidate before refetch.
    snapshotCache = null;
  }

  // Slow path: fresh capture in parallel with hash compute. If the
  // capture fails because a chart/object is selected (recoverable),
  // programmatically click into A1 and retry once. Cell-typing mode
  // is NOT recoverable — re-throws so the caller can prompt the user.
  let snapshot: WorkbookContext;
  let hash: string;
  try {
    [snapshot, hash] = await Promise.all([
      captureWorkbookSnapshot(),
      computeWorkbookHash(),
    ]);
  } catch (err) {
    if (isInvalidSelectionError(err) && (await recoverFromInvalidSelection())) {
      [snapshot, hash] = await Promise.all([
        captureWorkbookSnapshot(),
        computeWorkbookHash(),
      ]);
    } else {
      throw err;
    }
  }
  snapshotCache = { snapshot, hash, at: now };
  return { snapshot, hash };
}

/** SHA-256 fingerprint of sheet names + headers — identifies "same
 *  workbook across edits" for memory retrieval. */
export async function computeWorkbookHash(): Promise<string> {
  if (!isInOffice() || typeof Excel === 'undefined') return '';
  try {
    const fp = await Excel.run(async (ctx) => {
      const sheets = ctx.workbook.worksheets;
      sheets.load(['items/name']);
      await ctx.sync();
      return sheets.items.map((s) => s.name).sort().join('|');
    });
    return await sha256(fp);
  } catch {
    return '';
  }
}

async function sha256(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function stripSheetPrefix(address: string): string {
  const idx = address.indexOf('!');
  return idx >= 0 ? address.slice(idx + 1) : address;
}

function columnLetter(col: number): string {
  let s = '';
  while (col > 0) {
    const rem = (col - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    col = Math.floor((col - 1) / 26);
  }
  return s;
}

/**
 * Build a CellData grid from a synced Excel.Range.
 * Each cell carries its ABSOLUTE Excel address ("B100" not "A1").
 * - startCol/startRow are 1-indexed (A=1) and tell us where the slice
 *   originates so addresses come out right.
 * - Hard-stops at ACTIVE_SHEET_CELL_CAP populated cells so a dense
 *   slice can't blow the request body on huge sheets.
 */
function buildGrid(
  range: Excel.Range,
  startCol: number,
  startRow: number,
): CellData[][] {
  const values = (range.values ?? []) as unknown[][];
  const formulas = (range.formulas ?? []) as unknown[][];
  const types = (range.valueTypes ?? []) as unknown[][];
  const grid: CellData[][] = [];
  let populated = 0;
  for (let r = 0; r < values.length; r++) {
    const row: CellData[] = [];
    const vRow = values[r] ?? [];
    for (let c = 0; c < vRow.length; c++) {
      const cellAddr = `${columnLetter(startCol + c)}${startRow + r}`;
      const v = vRow[c];
      const f = formulas[r]?.[c];
      const t = mapValueType(types[r]?.[c]);
      if (v === '' && (!f || f === '')) {
        row.push({ address: cellAddr });
        continue;
      }
      row.push({
        address: cellAddr,
        value: v as CellData['value'],
        formula: typeof f === 'string' && f.startsWith('=') ? f : undefined,
        type: t,
      });
      populated++;
    }
    grid.push(row);
    if (populated >= ACTIVE_SHEET_CELL_CAP) {
      break;
    }
  }
  return grid;
}

// detectNumericColumns walks the head-sample cells of an ACTIVE sheet
// record and returns the 1-indexed Excel column numbers whose first few
// data rows look numeric. Header-aware: when sheet.headers is populated
// we skip row index 0 (header), so a textual header above numeric data
// doesn't disqualify the column.
//
// Threshold of 60% lets a column survive one "TOTAL" / "NA" / footer
// label without being misclassified — common on financial sheets that
// mix label rows in with numeric data.
function detectNumericColumns(sheet: SheetData): { col: number; header: string }[] {
  if (!sheet.cells || sheet.cells.length === 0) return [];
  const startCol = sheet.head_origin?.col ?? 1;
  // Skip header row when one was captured.
  const dataStart = sheet.headers && sheet.headers.length > 0 ? 1 : 0;
  const rows = sheet.cells.slice(dataStart, dataStart + AGGREGATE_DETECT_SAMPLE);
  if (rows.length === 0) return [];

  let width = 0;
  for (const r of rows) width = Math.max(width, r.length);

  const out: { col: number; header: string }[] = [];
  for (let c = 0; c < width; c++) {
    let numeric = 0;
    let total = 0;
    for (const row of rows) {
      const cell = row[c];
      if (!cell || cell.value == null || cell.value === '') continue;
      total++;
      if (typeof cell.value === 'number') numeric++;
    }
    if (total === 0) continue;
    if (numeric / total >= AGGREGATE_DETECT_THRESHOLD) {
      const headerText = sheet.headers?.[c] ?? '';
      out.push({ col: startCol + c, header: headerText });
    }
  }
  return out;
}

// computeColumnAggregates evaluates 9 metrics per numeric column via the
// Excel.Functions API (ExcelApi 1.2). Excel computes each function itself
// — NOTHING is written into the sheet, so the capture leaves the user's
// undo stack, autosave dirty-state, and worksheets.onChanged untouched
// (the old temp-formula approach at XFD1048500 broke all three). Returns
// one ColumnAggregate per column, omitting columns whose COUNT came back
// zero (no numeric data in the full data range).
//
// Failure mode is graceful — caller catches and falls back to an
// aggregate-less snapshot (e.g. transient Office.js / cell-edit-mode
// block). Per-metric evaluation errors land on FunctionResult.error, not
// as sync throws — we skip those metrics instead of failing the sheet.
async function computeColumnAggregates(
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  cols: { col: number; header: string }[],
  startRow: number,
  endRow: number,
): Promise<ColumnAggregate[]> {
  if (cols.length === 0 || endRow < startRow) return [];

  // startRow/endRow are ABSOLUTE 1-indexed sheet rows: the first and last
  // rows of the sheet's used range (NOT a row count — a used range of
  // A5:K200 arrives as startRow=5, endRow=200), so the ranges below hit
  // the real data even when the sheet doesn't start at row 1.
  // First data row is one past the header (typical Excel layout).
  // detectNumericColumns already filtered out non-numeric headers, so
  // we trust startRow points at the header and dataStart = startRow + 1.
  const dataStart = startRow + 1;
  const dataEnd = endRow;
  if (dataEnd < dataStart) return [];

  // Queue every metric for every column, then resolve them ALL in one
  // sync. Order matters — the result array is read by index below to
  // build the same ColumnAggregate shape the backend already consumes.
  const fns = ctx.workbook.functions;
  const perColumn: Excel.FunctionResult<number>[][] = [];
  for (const { col } of cols) {
    const colLet = columnLetter(col);
    const range = sheet.getRange(`${colLet}${dataStart}:${colLet}${dataEnd}`);
    const results = [
      fns.count(range),
      fns.sum(range),
      fns.average(range),
      fns.min(range),
      fns.max(range),
      fns.median(range),
      fns.stDev_S(range),
      fns.percentile_Inc(range, 0.25),
      fns.percentile_Inc(range, 0.75),
    ];
    for (const r of results) r.load(['value', 'error']);
    perColumn.push(results);
  }
  await ctx.sync();

  const out: ColumnAggregate[] = [];
  for (let i = 0; i < cols.length; i++) {
    const [count, sum, mean, min, max, median, stdev, p25, p75] =
      perColumn[i].map(functionResultNumber);
    if (!count || count <= 0) continue; // empty / non-numeric column → skip
    out.push({
      column: columnLetter(cols[i].col),
      header: cols[i].header || undefined,
      count,
      sum: sum ?? 0,
      mean: mean ?? 0,
      min: min ?? 0,
      max: max ?? 0,
      median: median ?? undefined,
      stdev: stdev ?? undefined,
      p25: p25 ?? undefined,
      p75: p75 ?? undefined,
    });
  }
  return out;
}

// functionResultNumber coerces a FunctionResult into a number, or null
// when the evaluation errored (result.error set, e.g. #DIV/0! from
// STDEV.S over a single row) or returned a non-numeric value. Handles
// number-as-string for locales where Excel echoes formatted text.
function functionResultNumber(r: Excel.FunctionResult<number>): number | null {
  if (r.error) return null;
  const v: unknown = r.value;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const f = parseFloat(v.replace(/,/g, ''));
    return Number.isFinite(f) ? f : null;
  }
  return null;
}

function mapValueType(t: unknown): CellData['type'] | undefined {
  if (typeof t !== 'string') return undefined;
  const lower = t.toLowerCase();
  if (lower.includes('error')) return 'error';
  if (lower.includes('string')) return 'string';
  if (lower.includes('double') || lower.includes('integer')) return 'number';
  if (lower.includes('boolean')) return 'boolean';
  if (lower.includes('empty')) return 'empty';
  return undefined;
}

// Back-compat alias; existing hook imports this name.
export const captureWorkbookContext = captureActiveContext;

// =============================================================================
// Vision: capture the current selection (or used range) as a PNG so the
// LLM can "see" the workbook. Returns base64 without any data-URL prefix.
// =============================================================================

export interface CapturedImage {
  mediaType: 'image/png';
  data: string;           // base64, no data: prefix
  label: string;          // e.g. "Sheet1!A1:E20" — shown in the UI chip
}

/**
 * Captures a PNG of the user's current selection. Falls back to the
 * active sheet's used range when the selection is empty or a single
 * cell. Throws a human-readable error when the Excel build doesn't
 * support Range.getImage().
 */
export async function captureSelectionImage(): Promise<CapturedImage> {
  if (!isInOffice() || typeof Excel === 'undefined') {
    throw new Error('Not running in Excel — screenshots are only available inside the Excel add-in.');
  }

  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');

    // Start with the user's selection; escalate to used range if selection
    // is a single cell (a 1×1 screenshot isn't useful for the LLM).
    let range = ctx.workbook.getSelectedRange();
    range.load(['address', 'rowCount', 'columnCount']);
    await ctx.sync();

    if (range.rowCount === 1 && range.columnCount === 1) {
      const used = sheet.getUsedRange(true);
      used.load(['address', 'rowCount', 'columnCount']);
      try {
        await ctx.sync();
        if (used.rowCount > 0 && used.columnCount > 0) {
          range = used;
        }
      } catch {
        // getUsedRange can throw on a brand-new empty sheet — keep the
        // single-cell selection in that case.
      }
    }

    // `getImage()` is only available on Excel JS 1.9+. If the runtime
    // doesn't expose it, we surface a helpful error rather than
    // crashing with an unreadable RichApi message.
    const rangeAny = range as unknown as {
      getImage?: () => { value: string; load: () => void };
    };
    if (typeof rangeAny.getImage !== 'function') {
      throw new Error('This build of Excel does not support screenshots. Update Excel or try Excel for the web.');
    }

    const result = rangeAny.getImage!();
    await ctx.sync();
    const base64 = String(result.value || '').replace(/^data:image\/png;base64,/, '');
    if (!base64) {
      throw new Error('Excel returned an empty screenshot. Try selecting a smaller range.');
    }

    return {
      mediaType: 'image/png',
      data: base64,
      label: `${sheet.name}!${range.address.split('!').pop() || range.address}`,
    };
  });
}

// =============================================================================
// Phase 4: apply approved changes via Office.js
// =============================================================================

import type {
  ProposedChange,
  ChangeType,
  CellFormat,
  ChartSpec,
  PivotSpec,
  ConditionalFormatSpec,
  SortSpec,
  ResizeSpec,
  FreezeSpec,
  FindReplaceSpec,
  FilterSpec,
  TextToColumnsSpec,
  NamedRangeSpec,
  DataValidationSpec,
  ProtectSpec,
  CommentSpec,
  GoalSeekSpec,
  SensitivityTableSpec,
  ScenariosSpec,
  RemoveDuplicatesSpec,
  TableSpec,
  ChartUpdateSpec,
  DeleteRowsWhereSpec,
} from '@/types/chat';

export interface ApplyResult {
  appliedIds: string[];
  failed: Array<{ id: string; reason: string }>;
}

/**
 * Applies approved changes in phase order so structural ops line up
 * with the cell writes that depend on them:
 *
 *   Phase 1: create_sheet (so subsequent writes see the target tab)
 *   Phase 2: rename_sheet (references update in-place for cell writes
 *            below, which use the NEW name)
 *   Phase 3: cell / format / clear / insert / delete_range writes,
 *            then charts/pivots, then protect_sheet (AFTER writes —
 *            protecting first would AccessDenied every write)
 *   Phase 4: delete_sheet (AFTER writes — deleting first would abort
 *            any write to that sheet)
 *
 * Each phase runs as its own Excel.run so Office.js can resolve proxies
 * against the post-phase state. NOTE: Office.js writes are NOT reliably
 * reversible via Excel's Ctrl+Z (especially on Excel for the web), so we
 * offer an explicit in-app Revert (revertAppliedChanges) for the
 * reversible change types instead of promising native undo.
 */
export interface ApplyOptions {
  // Fires after each applied change/section finishes its ctx.sync, with the
  // running count, total, and a human label ("Drawing chart on HR Dashboard").
  onProgress?: (done: number, total: number, label: string) => void;
  // Pause (ms) between VISUAL steps (charts/pivots) so each pops visibly.
  // Cell writes never pause — a 100-cell dashboard would crawl.
  stepDelayMs?: number;
  // Checkpoint between steps so a Stop press can break out cleanly.
  signal?: AbortSignal;
}

// RevertResult reports what an in-app revert could and couldn't undo.
export interface RevertResult {
  revertedIds: string[];
  // Changes whose type has no safe inverse (structural / object / data-
  // reshaping ops). The caller tells the user to use Excel's own undo.
  unrevertible: Array<{ id: string; type: ChangeType }>;
}

// revertibleTypes are the change types whose pre-state we captured
// (before / before_formula on the single cell they touch) and can safely
// restore. Structural ops (sort, remove_duplicates, delete_sheet,
// insert/delete row/col), object ops (charts/pivots/tables), and range/
// bulk writes have no reliable single-value inverse, so we don't pretend
// to undo them.
const revertibleTypes = new Set<ChangeType>([
  'set_value', 'set_formula', 'clear_cell',
]);

/**
 * revertAppliedChanges restores the captured before-state for the
 * reversible change types, newest-first. This is the honest replacement
 * for the old (false) "Ctrl+Z to undo" promise: it undoes exactly what it
 * can prove it can undo, and reports the rest as needing Excel's own undo.
 */
export async function revertAppliedChanges(changes: ProposedChange[]): Promise<RevertResult> {
  const revertedIds: string[] = [];
  const unrevertible: RevertResult['unrevertible'] = [];
  if (!isInOffice() || typeof Excel === 'undefined') {
    return { revertedIds, unrevertible: changes.map((c) => ({ id: c.id, type: c.type })) };
  }

  // Restore in REVERSE application order so overlapping writes unwind
  // correctly (last write over a cell is undone first).
  const ordered = [...changes].reverse();
  await Excel.run(async (ctx) => {
    for (const c of ordered) {
      if (!revertibleTypes.has(c.type) || !c.cell) {
        unrevertible.push({ id: c.id, type: c.type });
        continue;
      }
      try {
        const range = ctx.workbook.worksheets.getItem(c.sheet).getRange(c.cell);
        if (c.before_formula) {
          range.formulas = [[c.before_formula]];
        } else if (c.before !== undefined && c.before !== null) {
          range.values = [[c.before as string | number | boolean]];
        } else {
          // Cell was empty before — clear it.
          range.clear('Contents');
        }
        await ctx.sync();
        revertedIds.push(c.id);
      } catch {
        unrevertible.push({ id: c.id, type: c.type });
      }
    }
  });
  return { revertedIds, unrevertible };
}

// cfReplacedRanges tracks which (sheet!range) targets already had their
// pre-existing conditional formats cleared during the CURRENT apply run.
// Reset at the top of applyChanges; applies are serialized (applyFlow's
// re-entrancy guard), so module scope is safe.
const cfReplacedRanges = new Set<string>();

export async function applyChanges(changes: ProposedChange[], opts?: ApplyOptions): Promise<ApplyResult> {
  if (!isInOffice() || typeof Excel === 'undefined') {
    return {
      appliedIds: [],
      failed: changes.map((c) => ({ id: c.id, reason: 'Not running in Excel' })),
    };
  }
  cfReplacedRanges.clear();

  const appliedIds: string[] = [];
  const failed: ApplyResult['failed'] = [];
  const total = changes.length;
  const tick = (label: string) => opts?.onProgress?.(appliedIds.length, total, label);

  // Partition by phase. Preserve per-phase arrival order so the LLM
  // can depend on its own ordering for things like "create A, create B".
  // Chart + pivot run AFTER cell writes because they reference source
  // data that may have just been written.
  const createChanges: ProposedChange[] = [];
  const renameChanges: ProposedChange[] = [];
  const cellChanges: ProposedChange[] = [];
  const visualChanges: ProposedChange[] = [];
  const protectChanges: ProposedChange[] = [];
  const deleteChanges: ProposedChange[] = [];
  for (const c of changes) {
    switch (c.type) {
      case 'create_sheet':
        createChanges.push(c); break;
      case 'rename_sheet':
        renameChanges.push(c); break;
      case 'delete_sheet':
        deleteChanges.push(c); break;
      case 'create_chart':
      case 'update_chart':
      case 'create_pivot_table':
      case 'delete_pivot_table':
      case 'refresh_pivot_table':
      case 'delete_chart':
        visualChanges.push(c); break;
      // Sheet-level ops routed through Phase 2 (rename phase) so the
      // sheet exists by the time the op runs and doesn't race with
      // cell writes that reference it. unprotect_sheet must stay here —
      // it has to run BEFORE writes to a previously-protected sheet.
      case 'unprotect_sheet':
      case 'hide_sheet':
      case 'show_sheet':
      case 'reorder_sheet':
      case 'set_gridlines':
        renameChanges.push(c); break;
      // protect_sheet runs AFTER cell writes + visuals (post-write
      // protect step below). Protecting in Phase 2 made "write data +
      // protect sheet" in one diff fail every write with AccessDenied.
      case 'protect_sheet':
        protectChanges.push(c); break;
      default:
        cellChanges.push(c); break;
    }
  }

  const markFailed = (id: string, reason: string) => {
    if (!appliedIds.includes(id) && !failed.find((f) => f.id === id)) {
      failed.push({ id, reason });
    }
  };

  // Phase 1 — creates. If a sheet with the target name ALREADY exists
  // (e.g. from a prior failed attempt), we treat create as a no-op
  // success rather than a hard failure — subsequent writes still target
  // the same sheet, which is what the user asked for.
  if (createChanges.length > 0) {
    for (const c of createChanges) {
      try {
        await Excel.run(async (ctx) => {
          const ws = ctx.workbook.worksheets;
          // Pre-check: does the sheet already exist? getItemOrNullObject
          // is the idiomatic way — calling sync then reading .isNullObject.
          const existing = ws.getItemOrNullObject(c.sheet);
          existing.load('name');
          await ctx.sync();
          if (!existing.isNullObject) {
            // Already present — count as applied so downstream writes
            // can proceed. No "already exists" noise.
            return;
          }
          if (typeof c.position === 'number' && c.position > 0) {
            const added = ws.add(c.sheet);
            added.position = c.position;
          } else {
            ws.add(c.sheet);
          }
          await ctx.sync();
        });
        appliedIds.push(c.id);
        tick(`Created sheet ${c.sheet}`);
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'create_sheet failed';
        console.warn('create_sheet failed', { change: c, reason });
        markFailed(c.id, reason);
      }
    }
  }

  // Phase 2 — sheet-level ops: rename, unprotect, hide/show, reorder.
  // Grouped together because they all operate on the sheet object
  // itself (not on cells) and must run before cell writes so downstream
  // writes target the post-rename/post-unprotect state. protect_sheet
  // deliberately is NOT here — it runs after writes (see the post-write
  // protect step below).
  if (renameChanges.length > 0) {
    const queued: string[] = [];
    try {
      await Excel.run(async (ctx) => {
        for (const c of renameChanges) {
          try {
            const sheet = ctx.workbook.worksheets.getItem(c.sheet);
            switch (c.type) {
              case 'rename_sheet':
                sheet.name = c.new_name ?? c.sheet;
                break;
              case 'unprotect_sheet':
                sheet.protection.unprotect(c.protect?.password);
                break;
              case 'hide_sheet':
                sheet.visibility = 'Hidden' as Excel.SheetVisibility;
                break;
              case 'show_sheet':
                sheet.visibility = 'Visible' as Excel.SheetVisibility;
                break;
              case 'reorder_sheet':
                sheet.position = c.position ?? 0;
                break;
              case 'set_gridlines':
                // worksheet.showGridlines is ExcelApi 1.8; the manifest
                // floor is 1.7. Gate so a 1.7-only host degrades gracefully
                // (skip) instead of throwing and failing the whole batch.
                if (Office.context.requirements.isSetSupported('ExcelApi', '1.8')) {
                  sheet.showGridlines = c.show_gridlines ?? false;
                } else {
                  markFailed(c.id, 'hiding gridlines needs Excel that supports ExcelApi 1.8+');
                  continue;
                }
                break;
              default:
                markFailed(c.id, `unsupported sheet op: ${c.type}`);
                continue;
            }
            queued.push(c.id);
          } catch (err) {
            markFailed(c.id, err instanceof Error ? err.message : `${c.type} failed`);
          }
        }
        await ctx.sync();
      });
      appliedIds.push(...queued);
      opts?.onProgress?.(appliedIds.length, total, `Applied ${queued.length} sheet op(s)`);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'sheet-op batch failed';
      for (const id of queued) markFailed(id, reason);
    }
  }

  // cfKeys added to cfReplacedRanges by queueCellChange since the last
  // sync boundary. A key lands in the set when its clearAll is QUEUED —
  // if the sync that should flush it then FAILS, the clearAll never took
  // effect, but the key would survive and make the replay skip clearAll
  // (old rules from previous turns survive → conditional formats stack
  // instead of replacing). The Phase 3 paths reset this before queueing
  // and retract exactly these keys when their sync fails, so replays
  // re-issue clearAll while keys from successful syncs stay put.
  let cfKeysQueuedThisSync: string[] = [];

  // queueCellChange queues the Office.js ops for ONE cell-level change
  // against the given run context. Returns false when the change failed
  // validation (markFailed already recorded the reason) and queued
  // nothing; throws on hard Office.js errors. Extracted so the chunked
  // fast path and the one-at-a-time attribution path in Phase 3 share
  // exactly the same per-type logic.
  const queueCellChange = async (ctx: Excel.RequestContext, c: ProposedChange): Promise<boolean> => {
    const sheet = ctx.workbook.worksheets.getItem(c.sheet);
    switch (c.type) {
      case 'set_value': {
        const range = sheet.getRange(c.cell!);
        range.values = [[c.after ?? null]];
        break;
      }
      case 'set_formula': {
        const range = sheet.getRange(c.cell!);
        range.formulas = [[c.after_formula ?? '']];
        break;
      }
      case 'set_range': {
        // NEVER hand Office.js a grid containing null — a null-holed
        // labels write overlapping a formulas write silently lost the
        // formulas in production (HR Dashboard incident, 2026-07-10).
        // Both grids decompose into dense sub-rectangles; holes (null in
        // values, ''/null in formulas = "no formula here" per schema)
        // are simply never touched.
        const range = sheet.getRange(c.range!);
        const writeRuns = (grid: unknown[][], asFormulas: boolean): void => {
          for (const run of nonNullColumnRuns(grid)) {
            const target = range
              .getCell(run.row, run.col)
              .getResizedRange(run.values.length - 1, run.values[0].length - 1);
            if (asFormulas) target.formulas = run.values as string[][];
            else target.values = run.values as unknown[][];
          }
        };
        if (c.formulas && c.formulas.length > 0) {
          writeRuns(c.formulas.map((row) => row.map((f) => (f ? f : null))), true);
        }
        if (c.values && c.values.length > 0) {
          writeRuns(
            c.values.map((row, r) =>
              row.map((v, cc) => (c.formulas?.[r]?.[cc] ? null : (v as unknown))),
            ),
            false,
          );
        }
        break;
      }
      case 'format_cells': {
        const target = c.cell ?? c.range;
        if (!target) break;
        const range = sheet.getRange(target);
        // Inside-border edges (InsideHorizontal/InsideVertical) throw
        // on a 1x1 range, which would abort the whole shared sync.
        const singleCell = !target.includes(':');
        applyFormat(range, c.format ?? {}, singleCell);
        break;
      }
      case 'clear_cell': {
        const range = sheet.getRange(c.cell!);
        range.clear('Contents');
        break;
      }
      case 'delete_range': {
        const range = sheet.getRange(c.range!);
        range.delete('Up');
        break;
      }
      case 'apply_conditional_format': {
        const target = c.cell ?? c.range;
        if (!target || !c.conditional) {
          markFailed(c.id, 'conditional format needs range + spec');
          return false;
        }
        const range = sheet.getRange(target);
        // REPLACE, don't stack. conditionalFormats.add() appends the new
        // rule BELOW existing ones in Excel's priority list, so "change
        // the highlight to green" visibly did nothing — the old rule kept
        // winning while the UI truthfully said "applied". Clear the
        // target range's existing rules first, but only ONCE per range
        // per apply run, so one diff staging multiple rules on the same
        // range (">90 green AND <50 red") still stacks its OWN rules.
        const cfKey = `${c.sheet}!${target}`.toUpperCase();
        if (!cfReplacedRanges.has(cfKey)) {
          range.conditionalFormats.clearAll();
          cfReplacedRanges.add(cfKey);
          // Recorded per sync so a failed chunk/change sync can retract
          // the key (see cfKeysQueuedThisSync above) — otherwise the
          // replay would skip clearAll and stack rules again.
          cfKeysQueuedThisSync.push(cfKey);
        }
        applyConditionalFormat(range, c.conditional);
        break;
      }
      case 'sort_range': {
        if (!c.range || !c.sort || !c.sort.keys?.length) {
          markFailed(c.id, 'sort needs range + keys');
          return false;
        }
        applySortRange(sheet.getRange(c.range), c.sort);
        break;
      }
      // --- Tier 1 structural / layout ops -----------------------
      case 'delete_row': {
        applyDeleteRow(sheet, c.cell ?? c.range ?? '', c.count ?? 1);
        break;
      }
      case 'delete_column': {
        applyDeleteColumn(sheet, c.cell ?? c.range ?? '', c.count ?? 1);
        break;
      }
      case 'resize_column': {
        if (!c.resize) { markFailed(c.id, 'resize_column missing spec'); return false; }
        applyResizeColumn(sheet, c.cell ?? c.range ?? '', c.resize);
        break;
      }
      case 'resize_row': {
        if (!c.resize) { markFailed(c.id, 'resize_row missing spec'); return false; }
        applyResizeRow(sheet, c.cell ?? c.range ?? '', c.resize);
        break;
      }
      case 'merge_cells': {
        if (!c.range) { markFailed(c.id, 'merge_cells missing range'); return false; }
        sheet.getRange(c.range).merge(true);
        break;
      }
      case 'unmerge_cells': {
        if (!c.range) { markFailed(c.id, 'unmerge_cells missing range'); return false; }
        sheet.getRange(c.range).unmerge();
        break;
      }
      case 'freeze_panes': {
        applyFreezePanes(sheet, c.freeze);
        break;
      }
      case 'find_replace': {
        if (!c.find_replace) { markFailed(c.id, 'find_replace missing spec'); return false; }
        applyFindReplace(sheet, c.range, c.find_replace);
        break;
      }
      // Insert row/column types were previously routed via this
      // switch — the count field lets us honor N > 1 inserts.
      case 'insert_row': {
        applyInsertRow(sheet, c.cell ?? c.range ?? '', c.count ?? 1);
        break;
      }
      case 'insert_column': {
        applyInsertColumn(sheet, c.cell ?? c.range ?? '', c.count ?? 1);
        break;
      }
      // --- Tier 2 shape + reference ops ------------------------
      case 'filter_range': {
        if (!c.range || !c.filter) { markFailed(c.id, 'filter_range missing spec'); return false; }
        applyFilterRange(sheet, c.range, c.filter);
        break;
      }
      case 'clear_filter': {
        applyClearFilter(sheet);
        break;
      }
      case 'transpose_range': {
        if (!c.range || !c.cell) { markFailed(c.id, 'transpose needs range + destination'); return false; }
        await applyTransposeRange(sheet, c.range, c.cell);
        break;
      }
      case 'text_to_columns': {
        if (!c.range || !c.text_to_columns) { markFailed(c.id, 'text_to_columns missing spec'); return false; }
        await applyTextToColumns(sheet, c.range, c.text_to_columns);
        break;
      }
      case 'apply_data_validation': {
        const target = c.cell ?? c.range;
        if (!target || !c.validation) { markFailed(c.id, 'apply_data_validation missing spec'); return false; }
        applyDataValidation(sheet.getRange(target), c.validation);
        break;
      }
      case 'clear_data_validation': {
        const target = c.cell ?? c.range;
        if (!target) { markFailed(c.id, 'clear_data_validation missing target'); return false; }
        sheet.getRange(target).dataValidation.clear();
        break;
      }
      case 'create_named_range': {
        if (!c.named_range?.name || !c.named_range.refers) {
          markFailed(c.id, 'create_named_range missing name/refers');
          return false;
        }
        applyCreateNamedRange(ctx, c.named_range);
        break;
      }
      case 'delete_named_range': {
        if (!c.named_range?.name) {
          markFailed(c.id, 'delete_named_range missing name');
          return false;
        }
        applyDeleteNamedRange(ctx, c.named_range);
        break;
      }
      case 'add_comment': {
        if (!c.cell || !c.comment?.text) { markFailed(c.id, 'add_comment missing cell/text'); return false; }
        applyAddComment(ctx, c.sheet, c.cell, c.comment);
        break;
      }
      case 'delete_comment': {
        if (!c.cell) { markFailed(c.id, 'delete_comment missing cell'); return false; }
        await applyDeleteComment(ctx, c.sheet, c.cell);
        break;
      }
      // --- Phase 8 — bulk write efficiency ---------------------
      case 'clear_range': {
        if (!c.range) { markFailed(c.id, 'clear_range missing range'); return false; }
        // 'Contents' wipes values + formulas, preserves formats —
        // matches the user's mental model of "delete key on a range".
        sheet.getRange(c.range).clear('Contents');
        break;
      }
      case 'clear_all': {
        // Auto-detect the used range and wipe it. include_format
        // is encoded on c.after as a boolean.
        const includeFormat = c.after === true;
        // getUsedRange(true) THROWS at sync on an empty sheet; the
        // OrNullObject variant sets isNullObject=true instead, so an
        // empty sheet resolves as a no-op success.
        const used = sheet.getUsedRangeOrNullObject(true /* valuesOnly */);
        used.load(['address', 'isNullObject']);
        await ctx.sync();
        if (used.isNullObject || !used.address) {
          // Sheet already empty — no-op success.
          break;
        }
        sheet.getRange(used.address).clear(includeFormat ? 'All' : 'Contents');
        break;
      }
      case 'append_rows': {
        if (!c.values || c.values.length === 0) {
          markFailed(c.id, 'append_rows missing values');
          return false;
        }
        const used = sheet.getUsedRangeOrNullObject(true);
        used.load(['rowIndex', 'rowCount', 'columnIndex', 'columnCount']);
        await ctx.sync();
        let startRow = 0;
        let startCol = 0;
        if (!used.isNullObject) {
          startRow = (used.rowIndex ?? 0) + (used.rowCount ?? 0);
          startCol = used.columnIndex ?? 0;
        }
        // Optional column anchor override (c.cell carries 'B', 'C' etc).
        if (c.cell) {
          const colLetters = String(c.cell).replace(/[^A-Z]/gi, '').toUpperCase();
          if (colLetters) {
            startCol = colLetters
              .split('')
              .reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
          }
        }
        const rows = c.values.length;
        const cols = c.values[0]?.length ?? 0;
        const target = sheet.getRangeByIndexes(startRow, startCol, rows, cols);
        target.values = c.values as unknown[][];
        break;
      }
      case 'fill_series': {
        if (!c.range || !c.fill_series) {
          markFailed(c.id, 'fill_series missing range/spec');
          return false;
        }
        const range = sheet.getRange(c.range);
        range.load(['rowCount', 'columnCount']);
        await ctx.sync();
        const grid = expandFillSeries(c.fill_series, range.rowCount, range.columnCount);
        if (!grid) {
          markFailed(c.id, 'fill_series: could not expand sequence');
          return false;
        }
        range.values = grid;
        break;
      }
      case 'copy_range': {
        if (!c.copy_source || !c.copy_source.range) {
          markFailed(c.id, 'copy_range missing source');
          return false;
        }
        const dest = c.cell ?? c.range;
        if (!dest) {
          markFailed(c.id, 'copy_range missing destination');
          return false;
        }
        const srcSheet = ctx.workbook.worksheets.getItem(c.copy_source.sheet || c.sheet);
        const srcRange = srcSheet.getRange(c.copy_source.range);
        const destRange = sheet.getRange(dest);
        // Office.js copyType strings: 'All' | 'Formulas' | 'Values' | 'Formats'
        const mode = c.copy_source.mode ?? 'all';
        const copyType =
          mode === 'values'   ? 'Values'   :
          mode === 'formulas' ? 'Formulas' :
          mode === 'formats'  ? 'Formats'  : 'All';
        const transpose = mode === 'transpose';
        destRange.copyFrom(srcRange, copyType as Excel.RangeCopyType, false /* skipBlanks */, transpose);
        break;
      }
      case 'autofit_columns': {
        const target = c.range
          ? sheet.getRange(c.range)
          : sheet.getUsedRangeOrNullObject(true);
        target.load('isNullObject');
        await ctx.sync();
        // getUsedRangeOrNullObject returns isNullObject=true when sheet is empty.
        if ('isNullObject' in target && (target as Excel.Range).isNullObject) break;
        (target as Excel.Range).format.autofitColumns();
        break;
      }
      case 'autofit_rows': {
        const target = c.range
          ? sheet.getRange(c.range)
          : sheet.getUsedRangeOrNullObject(true);
        target.load('isNullObject');
        await ctx.sync();
        if ('isNullObject' in target && (target as Excel.Range).isNullObject) break;
        (target as Excel.Range).format.autofitRows();
        break;
      }
      // --- Tier 4: What-If Analysis -----------------------------
      case 'goal_seek': {
        if (!c.goal_seek) {
          markFailed(c.id, 'goal_seek missing spec');
          return false;
        }
        await applyGoalSeek(ctx, sheet, c.goal_seek);
        break;
      }
      case 'sensitivity_table': {
        if (!c.sensitivity) {
          markFailed(c.id, 'sensitivity_table missing spec');
          return false;
        }
        await applySensitivityTable(ctx, sheet, c.sensitivity);
        break;
      }
      case 'scenarios': {
        if (!c.scenarios) {
          markFailed(c.id, 'scenarios missing spec');
          return false;
        }
        await applyScenarios(ctx, sheet, c.scenarios);
        break;
      }
      // --- Phase 4: competitor-parity ops ----------------------
      case 'remove_duplicates': {
        if (!c.range) { markFailed(c.id, 'remove_duplicates missing range'); return false; }
        await applyRemoveDuplicates(ctx, sheet, c.range, c.remove_duplicates);
        break;
      }
      case 'create_table': {
        if (!c.range || !c.table?.name) { markFailed(c.id, 'create_table needs range + name'); return false; }
        applyCreateTable(sheet, c.range, c.table);
        break;
      }
      case 'delete_table': {
        if (!c.table?.name) { markFailed(c.id, 'delete_table missing name'); return false; }
        applyDeleteTable(ctx, c.table);
        break;
      }
      // --- Scalable bulk ops (criteria delete + native autofill) -----
      case 'delete_rows_where': {
        if (!c.range || !c.delete_rows_where) {
          markFailed(c.id, 'delete_rows_where missing range/criteria');
          return false;
        }
        const deleted = await applyDeleteRowsWhere(ctx, sheet, c.range, c.delete_rows_where);
        // ApplyResult carries no per-change success message, so surface
        // the count in the console for support/diagnostics. Zero matches
        // is a legitimate no-op success, not an error.
        console.info(`delete_rows_where: removed ${deleted} row(s) from ${c.sheet}!${c.range}`);
        break;
      }
      case 'fill_formula': {
        if (!c.range || !c.after_formula) {
          markFailed(c.id, 'fill_formula missing range/formula');
          return false;
        }
        await applyFillFormula(sheet, c.range, c.after_formula);
        break;
      }
      default:
        markFailed(c.id, `unsupported change type: ${c.type}`);
        return false;
    }
    return true;
  };

  // Phase 3 — cell-level writes. Use renamed sheet names if any.
  // Chunked: queue up to CELL_WRITE_CHUNK changes then ONE ctx.sync() —
  // syncing per change made a 200-write dashboard pay 200 Office round-
  // trips. A failed chunk sync can't tell us WHICH change poisoned it,
  // so that chunk re-runs one-change-at-a-time (the old slow path) to
  // attribute the exact failing op with its own reason (failure strings
  // + partial status keep working). Idempotent value/format writes are
  // safe to re-run over a half-applied chunk. Ordering is preserved:
  // pending chunks flush in arrival order, the slow path replays a chunk
  // in the same order, and non-idempotent structural ops run in arrival
  // order between chunks (see NON_IDEMPOTENT_TYPES below).
  const CELL_WRITE_CHUNK = 25;

  // Office.js does NOT roll back ops that already executed before the
  // failing op in the same ctx.sync() — a failed chunk sync leaves the
  // chunk HALF-APPLIED. Replaying is only safe when every op in the chunk
  // is idempotent (writing the same value/format twice is a no-op). The
  // types below are NOT idempotent: a replayed insert_row shifts data
  // twice, a replayed delete_range eats extra rows, sort / fill_formula /
  // remove_duplicates reshuffle or rewrite already-mutated data. They must
  // NEVER share a chunk with other changes NOR be replayed blindly: each
  // one first flushes the pending chunk, then runs ALONE with its own
  // immediate sync; a failure marks only that change failed (no replay).
  const NON_IDEMPOTENT_TYPES = new Set<ChangeType>([
    'insert_row', 'insert_column', 'delete_row', 'delete_column',
    'delete_range', 'delete_rows_where', 'fill_formula', 'copy_range',
    'transpose_range', 'text_to_columns', 'sort_range', 'remove_duplicates',
  ]);

  // applyIdempotentChunk runs one ≤CELL_WRITE_CHUNK batch of idempotent
  // changes: fast path (one sync for the whole chunk), then the slow
  // one-sync-per-change attribution path when the batch sync fails.
  const applyIdempotentChunk = async (chunk: ProposedChange[]): Promise<void> => {
    const chunkQueued: string[] = [];
    let fastOk = false;
    cfKeysQueuedThisSync = [];
    try {
      await Excel.run(async (ctx) => {
        for (const c of chunk) {
          if (opts?.signal?.aborted) break;
          // Validation failures (return false) markFailed themselves and
          // queue nothing; hard throws abandon the whole fast path so
          // the slow path below attributes them to the exact change —
          // catching here would pin an earlier change's poison on
          // whichever change happened to sync next.
          if (await queueCellChange(ctx, c)) chunkQueued.push(c.id);
        }
        await ctx.sync();
      });
      fastOk = true;
    } catch (err) {
      // The failed sync discarded this chunk's queued clearAll ops —
      // retract the cfKeys it recorded so the replay re-issues clearAll
      // (otherwise old conditional-format rules survive and stack). Keys
      // from previously successful chunks stay put.
      for (const k of cfKeysQueuedThisSync) cfReplacedRanges.delete(k);
      cfKeysQueuedThisSync = [];
      console.warn('cell-write chunk failed — replaying one-by-one', {
        size: chunk.length,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
    if (fastOk) {
      appliedIds.push(...chunkQueued);
      if (chunkQueued.length > 0) {
        const last = chunk.find((c) => c.id === chunkQueued[chunkQueued.length - 1])!;
        tick(`Writing ${last.sheet}!${last.cell ?? last.range ?? ''}`);
      }
      return;
    }
    // Slow path: re-apply THIS chunk with a sync per change so one bad
    // write doesn't poison the rest, and a single failing change
    // reports its OWN reason (e.g. "sheet not found", "invalid range")
    // instead of "cell-write batch failed" for everyone.
    try {
      await Excel.run(async (ctx) => {
        for (const c of chunk) {
          // Deterministic validation failures from the fast pass stay
          // failed — replaying them would just repeat the markFailed.
          if (failed.some((f) => f.id === c.id)) continue;
          const cfMark = cfKeysQueuedThisSync.length;
          try {
            if (!(await queueCellChange(ctx, c))) continue;
            try {
              await ctx.sync();
              appliedIds.push(c.id);
              tick(`Writing ${c.sheet}!${c.cell ?? c.range ?? ''}`);
            } catch (syncErr) {
              // Same cfKey retraction as the fast path, scoped to the
              // keys THIS change queued — a later CF change on the same
              // range must still get its clearAll.
              for (const k of cfKeysQueuedThisSync.slice(cfMark)) cfReplacedRanges.delete(k);
              cfKeysQueuedThisSync.length = cfMark;
              const reason = syncErr instanceof Error ? syncErr.message : `${c.type} sync failed`;
              console.warn('cell-write sync failed', { change: c, reason });
              markFailed(c.id, reason);
            }
          } catch (err) {
            for (const k of cfKeysQueuedThisSync.slice(cfMark)) cfReplacedRanges.delete(k);
            cfKeysQueuedThisSync.length = cfMark;
            const reason = err instanceof Error ? err.message : 'apply failed';
            console.warn('cell-write apply failed', { change: c, reason });
            markFailed(c.id, reason);
          }
          if (opts?.signal?.aborted) break; // Stop pressed — halt remaining cell writes
        }
      });
    } catch (err) {
      // Excel.run itself died (host gone, doc closed) — every change in
      // this chunk that isn't already resolved shares the batch reason.
      const reason = err instanceof Error ? err.message : 'cell-write batch failed';
      console.warn('cell-write batch failed', { reason });
      for (const c of chunk) markFailed(c.id, reason);
    }
  };

  // applyStructuralChange runs ONE non-idempotent change in its own
  // Excel.run with an immediate sync. On failure only THIS change is
  // marked failed — never replayed, because Office.js may have partially
  // executed it and a re-run would mutate the sheet a second time
  // (double row shift, double delete, re-sort of half-sorted data).
  const applyStructuralChange = async (c: ProposedChange): Promise<void> => {
    try {
      let queued = false;
      await Excel.run(async (ctx) => {
        queued = await queueCellChange(ctx, c);
        if (queued) await ctx.sync();
      });
      if (queued) {
        appliedIds.push(c.id);
        tick(`Writing ${c.sheet}!${c.cell ?? c.range ?? ''}`);
      }
    } catch (err) {
      const reason = err instanceof Error ? err.message : `${c.type} failed`;
      console.warn('structural change failed — not replayed', { change: c, reason });
      markFailed(c.id, reason);
    }
  };

  if (cellChanges.length > 0) {
    let pending: ProposedChange[] = [];
    const flushPending = async (): Promise<void> => {
      if (pending.length === 0) return;
      const chunk = pending;
      pending = [];
      await applyIdempotentChunk(chunk);
    };
    for (const c of cellChanges) {
      if (opts?.signal?.aborted) break; // Stop pressed — halt remaining cell writes
      if (NON_IDEMPOTENT_TYPES.has(c.type)) {
        // Flush queued idempotent writes first so this structural op
        // never shares a sync with them (non-rollback semantics above).
        await flushPending();
        if (opts?.signal?.aborted) break;
        await applyStructuralChange(c);
        continue;
      }
      pending.push(c);
      if (pending.length >= CELL_WRITE_CHUNK) await flushPending();
    }
    if (!opts?.signal?.aborted) await flushPending();
  }

  // Phase 3.5 — charts + pivots. Run AFTER cell writes so the source
  // data they reference is present. Each gets its own Excel.run because
  // chart/pivot APIs need a clean proxy state to bind against.
  //
  // Charts that DON'T carry an explicit anchor_cell auto-tile into a
  // non-overlapping 2-column grid per sheet. Placement is occupancy-aware
  // (see chartLayout.ts): each chart scans the charts ALREADY on the sheet
  // — prior turns' charts included — and takes the first free tile. A
  // per-run counter here previously restarted at 0 on every Apply, which
  // stacked follow-up-turn charts pixel-exact on top of the first build.
  for (const c of visualChanges) {
    let queued = false;
    try {
      await Excel.run(async (ctx) => {
        const sheet = ctx.workbook.worksheets.getItem(c.sheet);
        if (c.type === 'create_chart' && c.chart) {
          await applyCreateChart(ctx, sheet, c.chart);
        } else if (c.type === 'create_pivot_table' && c.pivot) {
          await applyCreatePivot(ctx, c.sheet, c.pivot);
        } else if (c.type === 'delete_pivot_table' && c.pivot?.name) {
          applyDeletePivot(ctx, c.pivot.name);
        } else if (c.type === 'refresh_pivot_table' && c.pivot?.name) {
          applyRefreshPivot(ctx, c.pivot.name);
        } else if (c.type === 'delete_chart' && c.chart?.title) {
          await applyDeleteChart(sheet, c.chart.title);
        } else if (c.type === 'update_chart' && c.chart_update?.name) {
          await applyUpdateChart(ctx, sheet, c.chart_update);
        } else {
          markFailed(c.id, `${c.type} missing spec`);
          return;
        }
        queued = true;
        await ctx.sync();
      });
      if (queued) {
        appliedIds.push(c.id);
        // This is the visible "charts pop one-by-one" moment. The chart is
        // already on screen (ctx.sync resolved), so the pause lets the eye
        // catch each one before the next.
        tick(c.type === 'create_chart' ? `Drawing chart on ${c.sheet}` : `Building ${c.type.replace(/_/g, ' ')}`);
        if (opts?.stepDelayMs) await delay(opts.stepDelayMs);
      }
    } catch (err) {
      // DIAGNOSABILITY: a bare Office.js message ("The requested resource
      // doesn't exist") cost three debugging rounds in production because
      // it never said WHICH resource for WHICH change. Name the change,
      // its target sheet, and the source range in every failure.
      const what = c.type === 'create_pivot_table' ? `pivot "${c.pivot?.name}" (source ${c.pivot?.source_range})`
        : c.type === 'create_chart' ? `chart "${c.chart?.title ?? c.chart?.chart_type}" (source ${c.chart?.source_range})`
        : c.type;
      const msg = err instanceof Error ? err.message : 'failed';
      console.error(`[apply] ${c.type} failed on sheet "${c.sheet}":`, err);
      markFailed(c.id, `${what} on sheet "${c.sheet}": ${msg}`);
    }
    if (opts?.signal?.aborted) {
      // Stop pressed — mark the not-yet-applied visuals as cancelled so the
      // diff resolves to 'partial' (not a misleading 'applied'), then stop.
      for (const rest of visualChanges) {
        if (!appliedIds.includes(rest.id) && !failed.some((f) => f.id === rest.id)) {
          markFailed(rest.id, 'cancelled');
        }
      }
      break;
    }
  }

  // Stop pressed during/after visuals — don't run deletes afterward.
  if (opts?.signal?.aborted) return { appliedIds, failed };

  // Phase 3.75 — protect_sheet AFTER cell writes + visuals, before
  // deletes. Protecting in Phase 2 locked the sheet before Phase 3 ran,
  // so "write data + protect sheet" in one diff failed every write with
  // AccessDenied. unprotect_sheet stays in Phase 2 for the mirror-image
  // reason (it must run BEFORE writes).
  if (protectChanges.length > 0) {
    const queued: string[] = [];
    try {
      await Excel.run(async (ctx) => {
        for (const c of protectChanges) {
          try {
            const sheet = ctx.workbook.worksheets.getItem(c.sheet);
            applyProtectSheet(sheet, c.protect);
            queued.push(c.id);
          } catch (err) {
            markFailed(c.id, err instanceof Error ? err.message : 'protect_sheet failed');
          }
        }
        await ctx.sync();
      });
      appliedIds.push(...queued);
      opts?.onProgress?.(appliedIds.length, total, `Protected ${queued.length} sheet(s)`);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'protect_sheet batch failed';
      for (const id of queued) markFailed(id, reason);
    }
  }

  // Phase 4 — deletes last, so writes aren't aborted.
  if (deleteChanges.length > 0) {
    const queued: string[] = [];
    try {
      await Excel.run(async (ctx) => {
        for (const c of deleteChanges) {
          try {
            const sheet = ctx.workbook.worksheets.getItem(c.sheet);
            sheet.delete();
            queued.push(c.id);
          } catch (err) {
            markFailed(c.id, err instanceof Error ? err.message : 'delete_sheet failed');
          }
        }
        await ctx.sync();
      });
      appliedIds.push(...queued);
      opts?.onProgress?.(appliedIds.length, total, `Removed ${queued.length} sheet(s)`);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'delete_sheet batch failed';
      for (const id of queued) markFailed(id, reason);
    }
  }

  return { appliedIds, failed };
}

function applyFormat(range: Excel.Range, f: CellFormat, singleCell = false) {
  if (f.number_format !== undefined && f.number_format !== '') {
    range.numberFormat = [[sanitizeNumberFormat(f.number_format)]];
  }
  if (f.font_family) range.format.font.name = f.font_family;
  if (f.bold !== undefined) range.format.font.bold = f.bold;
  if (f.italic !== undefined) range.format.font.italic = f.italic;
  if (f.underline !== undefined) {
    range.format.font.underline = f.underline ? 'Single' : 'None';
  }
  if (f.font_color) range.format.font.color = f.font_color;
  if (f.fill_color) range.format.fill.color = f.fill_color;
  if (f.font_size && f.font_size > 0) range.format.font.size = f.font_size;
  if (f.alignment) {
    range.format.horizontalAlignment =
      f.alignment === 'center' ? 'Center' : f.alignment === 'right' ? 'Right' : 'Left';
  }
  if (f.vertical_alignment) {
    // Wire value 'middle' is what models naturally say; Excel calls it Center.
    range.format.verticalAlignment =
      f.vertical_alignment === 'middle' ? 'Center'
        : f.vertical_alignment === 'bottom' ? 'Bottom' : 'Top';
  }
  if (f.wrap_text !== undefined) range.format.wrapText = f.wrap_text;
  if (f.border) {
    const edges = (f.border.edges && f.border.edges.length > 0)
      ? f.border.edges
      : ['top', 'bottom', 'left', 'right']; // default: box around the range
    const style = mapBorderStyle(f.border.style);
    const weight = mapBorderWeight(f.border.weight);
    for (const e of edges) {
      for (const idx of mapBorderEdge(e)) { // 'all' expands to outer + inside edges
        // Inside edges are invalid on a 1x1 range and would throw at sync,
        // aborting the whole shared cell-write batch — skip them there.
        if (singleCell && (idx === 'InsideHorizontal' || idx === 'InsideVertical')) continue;
        const b = range.format.borders.getItem(idx);
        b.style = style;
        if (f.border.color) b.color = f.border.color;
        b.weight = weight;
      }
    }
  }
}

function mapBorderEdge(e: string): Excel.BorderIndex[] {
  switch ((e || '').toLowerCase()) {
    case 'top': return ['EdgeTop' as Excel.BorderIndex];
    case 'bottom': return ['EdgeBottom' as Excel.BorderIndex];
    case 'left': return ['EdgeLeft' as Excel.BorderIndex];
    case 'right': return ['EdgeRight' as Excel.BorderIndex];
    case 'inside_horizontal': return ['InsideHorizontal' as Excel.BorderIndex];
    case 'inside_vertical': return ['InsideVertical' as Excel.BorderIndex];
    case 'all': return ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight', 'InsideHorizontal', 'InsideVertical'] as Excel.BorderIndex[];
    default: return [];
  }
}

function mapBorderStyle(s?: string): Excel.BorderLineStyle {
  switch ((s || '').toLowerCase()) {
    case 'dash': return 'Dash' as Excel.BorderLineStyle;
    case 'dot': return 'Dot' as Excel.BorderLineStyle;
    case 'double': return 'Double' as Excel.BorderLineStyle;
    case 'dashdot': return 'DashDot' as Excel.BorderLineStyle;
    case 'dashdotdot': return 'DashDotDot' as Excel.BorderLineStyle;
    case 'slantdashdot': return 'SlantDashDot' as Excel.BorderLineStyle;
    case 'none': return 'None' as Excel.BorderLineStyle;
    default: return 'Continuous' as Excel.BorderLineStyle;
  }
}

function mapBorderWeight(w?: string): Excel.BorderWeight {
  switch ((w || '').toLowerCase()) {
    case 'hairline': return 'Hairline' as Excel.BorderWeight;
    case 'medium': return 'Medium' as Excel.BorderWeight;
    case 'thick': return 'Thick' as Excel.BorderWeight;
    default: return 'Thin' as Excel.BorderWeight;
  }
}

// applySortRange maps our spec onto Excel.Range.sort.apply. LLM sends
// `orientation` as a free string; default = rows.
function applySortRange(range: Excel.Range, spec: SortSpec): void {
  const fields = spec.keys.map((k) => ({
    key: k.key,
    ascending: !!k.ascending,
    sortOn: mapSortOn(k.sort_on),
  }));
  const orientation = mapOrientation(spec.orientation);
  // sort.apply signature: (fields, matchCase?, hasHeaders?, orientation?, method?)
  range.sort.apply(
    fields,
    !!spec.match_case,
    !!spec.has_header,
    orientation,
  );
}

function mapSortOn(s?: string): Excel.SortOn {
  switch ((s || '').toLowerCase()) {
    case 'cell_color':
      return 'CellColor' as Excel.SortOn;
    case 'font_color':
      return 'FontColor' as Excel.SortOn;
    default:
      return 'Value' as Excel.SortOn;
  }
}

function mapOrientation(s?: string): Excel.SortOrientation {
  return (s || '').toLowerCase() === 'columns'
    ? ('Columns' as Excel.SortOrientation)
    : ('Rows' as Excel.SortOrientation);
}

// applyConditionalFormat dispatches on spec.type and configures the
// matching Excel.ConditionalFormat subtype. Everything is best-effort —
// any unknown rule type surfaces as a markFailed in the caller.
function applyConditionalFormat(range: Excel.Range, spec: ConditionalFormatSpec): void {
  const t = (spec.type || '').toLowerCase();
  switch (t) {
    case 'cell_value': {
      const cf = range.conditionalFormats.add(
        'CellValue' as Excel.ConditionalFormatType,
      );
      // Assign rule as a single object — reading cf.cellValue.rule
      // before sync triggers "property not available" because the proxy
      // hasn't materialised. The other cases below already use this
      // whole-object set pattern; cell_value was the outlier.
      //
      // Build the rule with `formula2` only when present — passing
      // `formula2: undefined` makes Office.js reject the rule with
      // "argument is invalid or missing or has an incorrect format".
      const rule: Excel.ConditionalCellValueRule = {
        operator: mapCellOperator(spec.operator),
        formula1: String(spec.values?.[0] ?? '0'),
      };
      if (spec.values && spec.values.length > 1 && spec.values[1] != null) {
        rule.formula2 = String(spec.values[1]);
      }
      cf.cellValue.rule = rule;
      applyCFFormat(cf.cellValue.format, spec.format);
      break;
    }
    case 'color_scale': {
      const cf = range.conditionalFormats.add(
        'ColorScale' as Excel.ConditionalFormatType,
      );
      const three = !!spec.mid_color;
      const criteria: Excel.ConditionalColorScaleCriteria = {
        minimum: {
          color: spec.min_color || '#F8696B',
          type: 'LowestValue' as Excel.ConditionalFormatColorCriterionType,
        },
        maximum: {
          color: spec.max_color || '#63BE7B',
          type: 'HighestValue' as Excel.ConditionalFormatColorCriterionType,
        },
      };
      if (three) {
        criteria.midpoint = {
          color: spec.mid_color || '#FFEB84',
          type: 'Percentile' as Excel.ConditionalFormatColorCriterionType,
          formula: '50',
        };
      }
      cf.colorScale.criteria = criteria;
      break;
    }
    case 'data_bar': {
      const cf = range.conditionalFormats.add(
        'DataBar' as Excel.ConditionalFormatType,
      );
      if (spec.bar_color) {
        cf.dataBar.positiveFormat.fillColor = spec.bar_color;
      }
      break;
    }
    case 'top_bottom': {
      const cf = range.conditionalFormats.add(
        'TopBottom' as Excel.ConditionalFormatType,
      );
      cf.topBottom.rule = {
        rank: spec.rank ?? 10,
        type: mapTopBottomType(!!spec.bottom_rank, !!spec.percent),
      };
      applyCFFormat(cf.topBottom.format, spec.format);
      break;
    }
    case 'text_contains': {
      const cf = range.conditionalFormats.add(
        'ContainsText' as Excel.ConditionalFormatType,
      );
      cf.textComparison.rule = {
        operator: mapTextOperator(spec.operator),
        text: spec.values?.[0] ?? '',
      };
      applyCFFormat(cf.textComparison.format, spec.format);
      break;
    }
    case 'above_below_average': {
      const cf = range.conditionalFormats.add(
        'PresetCriteria' as Excel.ConditionalFormatType,
      );
      cf.preset.rule = {
        criterion: spec.above
          ? ('AboveAverage' as Excel.ConditionalFormatPresetCriterion)
          : ('BelowAverage' as Excel.ConditionalFormatPresetCriterion),
      };
      applyCFFormat(cf.preset.format, spec.format);
      break;
    }
    case 'duplicate_values': {
      const cf = range.conditionalFormats.add(
        'PresetCriteria' as Excel.ConditionalFormatType,
      );
      cf.preset.rule = {
        criterion: 'DuplicateValues' as Excel.ConditionalFormatPresetCriterion,
      };
      applyCFFormat(cf.preset.format, spec.format);
      break;
    }
    case 'custom_formula': {
      // Normalize the formula:
      //   - Trim whitespace
      //   - Prepend '=' if the model omitted it
      //   - SMART FALLBACK: if values[0] is missing/empty/just "=", derive
      //     a sensible formula from other context instead of failing the
      //     entire apply batch. Empty formula was never useful anyway —
      //     better to skip gracefully than scare the user with an error.
      let formula = (spec.values?.[0] ?? '').trim();
      if (formula && !formula.startsWith('=')) {
        formula = '=' + formula;
      }
      if (!formula || formula === '=') {
        // Last-line safety net; backend validates first. Skip silently rather than abort the batch.
        console.warn('[applyChanges] custom_formula skipped: values[0] empty. spec=', spec);
        return;
      }
      const cf = range.conditionalFormats.add(
        'Custom' as Excel.ConditionalFormatType,
      );
      // Whole-object assignment via cast — same workaround as
      // cell_value above. TypeScript marks `rule` readonly but the
      // Office.js runtime accepts the assignment and avoids the
      // proxy-load issue we hit when reading cf.custom.rule.formula.
      try {
        (cf.custom as unknown as { rule: { formula: string } }).rule = { formula };
      } catch {
        // Fallback: per-field assignment in case whole-object isn't
        // accepted on this Office.js build.
        cf.custom.rule.formula = formula;
      }
      applyCFFormat(cf.custom.format, spec.format);
      break;
    }
    default:
      throw new Error(`unsupported conditional format type: ${spec.type}`);
  }
}

function mapCellOperator(op?: string): Excel.ConditionalCellValueOperator {
  switch ((op || '').toLowerCase()) {
    case 'greater_than': return 'GreaterThan' as Excel.ConditionalCellValueOperator;
    case 'less_than': return 'LessThan' as Excel.ConditionalCellValueOperator;
    case 'greater_than_or_equal': return 'GreaterThanOrEqual' as Excel.ConditionalCellValueOperator;
    case 'less_than_or_equal': return 'LessThanOrEqual' as Excel.ConditionalCellValueOperator;
    case 'equal_to': return 'EqualTo' as Excel.ConditionalCellValueOperator;
    case 'not_equal_to': return 'NotEqualTo' as Excel.ConditionalCellValueOperator;
    case 'between': return 'Between' as Excel.ConditionalCellValueOperator;
    case 'not_between': return 'NotBetween' as Excel.ConditionalCellValueOperator;
    default: return 'GreaterThan' as Excel.ConditionalCellValueOperator;
  }
}

function mapTextOperator(op?: string): Excel.ConditionalTextOperator {
  switch ((op || '').toLowerCase()) {
    case 'not_contains': return 'NotContains' as Excel.ConditionalTextOperator;
    case 'begins_with': return 'BeginsWith' as Excel.ConditionalTextOperator;
    case 'ends_with': return 'EndsWith' as Excel.ConditionalTextOperator;
    default: return 'Contains' as Excel.ConditionalTextOperator;
  }
}

function mapTopBottomType(bottom: boolean, percent: boolean): Excel.ConditionalTopBottomCriterionType {
  if (bottom && percent) return 'BottomPercent' as Excel.ConditionalTopBottomCriterionType;
  if (bottom) return 'BottomItems' as Excel.ConditionalTopBottomCriterionType;
  if (percent) return 'TopPercent' as Excel.ConditionalTopBottomCriterionType;
  return 'TopItems' as Excel.ConditionalTopBottomCriterionType;
}

// applyCFFormat bridges our CellFormat → Excel.ConditionalRangeFormat.
function applyCFFormat(target: Excel.ConditionalRangeFormat, f?: CellFormat): void {
  if (!f) return;
  if (f.number_format) target.numberFormat = sanitizeNumberFormat(f.number_format);
  if (f.bold !== undefined) target.font.bold = f.bold;
  if (f.italic !== undefined) target.font.italic = f.italic;
  if (f.underline !== undefined) {
    target.font.underline = (f.underline
      ? 'Single'
      : 'None') as Excel.ConditionalRangeFontUnderlineStyle;
  }
  if (f.font_color) target.font.color = f.font_color;
  if (f.fill_color) target.fill.color = f.fill_color;
}

// sanitizeNumberFormat makes a number-format string safe for Excel. Fixes
// two model mistakes that print the format CODE as literal text:
//   1. The whole format wrapped in quotes — "0%", "₹#,##0" — which Excel
//      treats as a literal string, so labels read "0%" / "₹#,##0" instead
//      of 5% / ₹3,00,000. We strip a fully-wrapping quote pair.
//   2. A bare ₹ (U+20B9), not a token Excel recognizes — quote it ("₹"#,##0)
//      so Excel renders the symbol then formats the number.
// Idempotent — leaves correct formats (0.0%, #,##0, [$₹-en-IN]#,##0) alone.
function sanitizeNumberFormat(fmt: string): string {
  if (!fmt) return fmt;
  let f = fmt.trim();
  // (1) Strip outer quotes only when they wrap the ENTIRE format (exactly
  // two quotes, at both ends) — e.g. "0%" or "₹#,##0". A partially-quoted
  // format like "₹"#,##0 (good) ends in 0, not a quote, so it's untouched.
  if (f.length >= 2 && f.startsWith('"') && f.endsWith('"')
    && (f.match(/"/g) || []).length === 2) {
    f = f.slice(1, -1);
  }
  // (2) Quote a bare ₹ so Excel formats it as currency.
  if (f.includes('₹') && !f.includes('"₹"') && !f.includes('[$₹')) {
    f = f.replace(/₹/g, '"₹"');
  }
  return f;
}

// findFreeChartAnchor picks the top-left cell of the first auto-tile slot
// not covered by any chart already on the sheet. One batched sync loads
// every existing chart's rect plus every candidate tile's rect; the pure
// pick lives in chartLayout.ts. Runs BEFORE the new chart is inserted so
// the chart never scans itself.
async function findFreeChartAnchor(ctx: Excel.RequestContext, sheet: Excel.Worksheet): Promise<Excel.Range> {
  const charts = sheet.charts;
  charts.load('items/top,items/left,items/width,items/height');
  const anchors: Excel.Range[] = [];
  const ends: Excel.Range[] = [];
  for (let i = 0; i < CHART_TILE.maxTiles; i++) {
    const { row, col } = tileAnchorCell(i);
    const a = sheet.getCell(row, col);
    const e = a.getOffsetRange(CHART_TILE.tileRows, CHART_TILE.tileCols);
    a.load(['top', 'left']);
    e.load(['top', 'left', 'width', 'height']);
    anchors.push(a);
    ends.push(e);
  }
  await ctx.sync();
  const occupied: Rect[] = charts.items.map((c) => ({
    top: c.top, left: c.left, width: c.width, height: c.height,
  }));
  const tiles: Rect[] = anchors.map((a, i) => ({
    top: a.top,
    left: a.left,
    width: ends[i].left + ends[i].width - a.left,
    height: ends[i].top + ends[i].height - a.top,
  }));
  const idx = firstFreeTileIndex(tiles, occupied, CHART_TILE.overlapEpsilonPt);
  if (idx >= 0) return anchors[idx];
  // Grid exhausted (>12 charts) — extend the same grid pattern past the
  // last slot so placement stays deterministic even in the degenerate case.
  console.warn(`[chartLayout] all ${CHART_TILE.maxTiles} chart tiles occupied — extending grid`);
  const { row, col } = tileAnchorCell(charts.items.length);
  return sheet.getCell(row, col);
}

// applyCreateChart inserts a chart into the target sheet. Everything in
// `spec` is free-string; if Office.js rejects chart_type it throws and
// our caller markFailed's the change.
async function applyCreateChart(ctx: Excel.RequestContext, sheet: Excel.Worksheet, spec: ChartSpec): Promise<void> {
  // Resolve auto-tile placement FIRST — the occupancy scan must not see
  // the chart we're about to add.
  const tileAnchor = spec.anchor_cell ? null : await findFreeChartAnchor(ctx, sheet);
  const dataRange = resolveRange(sheet, spec.source_range);
  // DATA GUARD: never insert a chart over a block whose value region is
  // blank — a "complete" dashboard with empty charts hides the real
  // failure (a lost summary-block write). Fail THIS change with the
  // actionable reason instead.
  dataRange.load('values');
  const app = ctx.workbook.application;
  app.load('calculationMode');
  await ctx.sync();
  let sourceEmpty = chartSourceLooksEmpty(dataRange.values);
  if (sourceEmpty && app.calculationMode !== 'Automatic') {
    // Manual-calc workbooks: freshly written formulas evaluate to ''
    // until a recalc. Recalculate once and re-check before failing —
    // otherwise every dashboard chart fails with a misleading error.
    app.calculate('Recalculate' as Excel.CalculationType);
    await ctx.sync();
    dataRange.load('values');
    await ctx.sync();
    sourceEmpty = chartSourceLooksEmpty(dataRange.values);
  }
  if (sourceEmpty) {
    throw new Error(
      `chart source ${spec.source_range} has an empty value column — its summary-block write did not land or did not evaluate, so the chart would render blank. Fix the block, then re-create the chart.`,
    );
  }
  let seriesBy = mapSeriesBy(spec.series_by);
  // DETERMINISTIC series orientation for the dominant dashboard shape:
  // a tall category|value block ("A9:B14"). Office's "Auto" sometimes
  // treats each ROW as a series there — N single-point series render as
  // an EMPTY-looking plot with only legend dots (real prod incident:
  // every dashboard chart blank). If the caller didn't explicitly pick,
  // and the source is taller than wide, series are the COLUMNS.
  if (seriesBy === ('Auto' as Excel.ChartSeriesBy)) {
    dataRange.load(['rowCount', 'columnCount']);
    await ctx.sync();
    if (dataRange.rowCount > dataRange.columnCount) {
      seriesBy = 'Columns' as Excel.ChartSeriesBy;
    }
  }
  const chart = sheet.charts.add(
    spec.chart_type as Excel.ChartType,
    dataRange,
    seriesBy,
  );
  const chartTypeLower = (spec.chart_type || '').toLowerCase();
  const isSlicedChart = chartTypeLower.includes('pie')
    || chartTypeLower.includes('doughnut') || chartTypeLower.includes('donut');
  if (spec.title) chart.title.text = spec.title;
  if (spec.x_axis_title) chart.axes.categoryAxis.title.text = spec.x_axis_title;
  if (spec.y_axis_title) chart.axes.valueAxis.title.text = spec.y_axis_title;
  if (spec.show_legend !== undefined) chart.legend.visible = spec.show_legend;
  if (spec.show_data_labels !== undefined) chart.dataLabels.showValue = spec.show_data_labels;
  if (spec.legend_position) {
    // Respect an explicit show_legend:false — don't force the legend back on.
    if (spec.show_legend !== false) chart.legend.visible = true;
    chart.legend.position = mapLegendPosition(spec.legend_position);
  }
  const wantsPercentLabels = !!spec.data_label_number_format
    && spec.data_label_number_format.includes('%');
  if (isSlicedChart && wantsPercentLabels) {
    // Pie/donut slice values are COUNTS. Applying a "0.0%" number format to a
    // count of 95 prints 9500% — the format multiplies by 100. What a pie %
    // label should show is share-of-total (95 → 95%), which is Excel's
    // built-in percentage label. Use that instead of the raw % numberFormat.
    chart.dataLabels.showPercentage = true;
    chart.dataLabels.showValue = false;
  } else if (spec.data_label_number_format
    && Office.context.requirements.isSetSupported('ExcelApi', '1.8')) {
    // ChartDataLabels.numberFormat is ExcelApi 1.8; the manifest floor is
    // 1.7. Gate so a 1.7-only host degrades gracefully (default label
    // format) instead of throwing and failing the whole chart insert.
    chart.dataLabels.numberFormat = sanitizeNumberFormat(spec.data_label_number_format);
  }
  if (spec.series_colors && spec.series_colors.length) {
    // getItemAt(i) on an out-of-range index throws at sync and aborts the
    // entire chart. Load the real series count first, then color only valid
    // indices, so over-specified colors degrade gracefully.
    const series = chart.series;
    series.load('count');
    await ctx.sync();
    const sliced = isSlicedChart;
    // Per-POINT colouring when slices ARE the categories: every pie/donut, AND
    // any single-series bar/column where the model supplied more than one
    // colour (it wants each category bar distinct, not one flat colour).
    if ((sliced || (series.count === 1 && spec.series_colors.length > 1)) && series.count > 0) {
      // ONE series whose POINTS are the categories. Colouring the series
      // paints every point the same (Male == Female). Colour each POINT so
      // every category is visually distinct.
      const pts = series.getItemAt(0).points;
      pts.load('count');
      await ctx.sync();
      for (let j = 0; j < spec.series_colors.length && j < pts.count; j++) {
        const hex = spec.series_colors[j];
        if (!hex) continue;
        pts.getItemAt(j).format.fill.setSolidColor(hex.replace(/^#/, ''));
      }
    } else {
      // Column/bar/line: one color per series.
      for (let i = 0; i < spec.series_colors.length && i < series.count; i++) {
        const hex = spec.series_colors[i];
        if (!hex) continue;
        // setSolidColor wants the hex WITHOUT the leading '#'.
        series.getItemAt(i).format.fill.setSolidColor(hex.replace(/^#/, ''));
      }
    }
  }
  if (spec.chart_font) {
    const cf = spec.chart_font;
    if (cf.name) chart.format.font.name = cf.name;
    if (cf.size !== undefined) chart.format.font.size = cf.size;
    if (cf.color) chart.format.font.color = cf.color;
    if (cf.bold !== undefined) chart.format.font.bold = cf.bold;
    if (cf.italic !== undefined) chart.format.font.italic = cf.italic;
  }
  // Premium defaults — deterministic, zero prompt tokens (chartDefaults.ts).
  // Everything here is ExcelApi 1.8 surface (border.clear, gapWidth,
  // doughnutHoleSize, axis numberFormat); a 1.7-only host skips the polish
  // rather than failing the insert.
  if (Office.context.requirements.isSetSupported('ExcelApi', '1.8')) {
    const d = premiumChartDefaults(chartTypeLower, spec);
    chart.format.border.clear();
    if (!isSlicedChart) {
      // Sliced charts have no value axis — touching it throws at sync.
      chart.axes.valueAxis.majorGridlines.format.line.color = d.gridlineColor;
      if (d.axisNumberFormat) {
        chart.axes.valueAxis.numberFormat = sanitizeNumberFormat(d.axisNumberFormat);
      }
    }
    if (d.gapWidth !== undefined || d.doughnutHoleSize !== undefined) {
      const series = chart.series;
      series.load('count');
      await ctx.sync();
      for (let i = 0; i < series.count; i++) {
        const s = series.getItemAt(i);
        if (d.gapWidth !== undefined) s.gapWidth = d.gapWidth;
        if (d.doughnutHoleSize !== undefined) s.doughnutHoleSize = d.doughnutHoleSize;
      }
    }
  }
  // Positioning: an explicit anchor_cell wins. Otherwise take the free
  // tile resolved before insertion (occupancy-aware — never covers an
  // existing chart, whichever turn created it).
  if (spec.anchor_cell) {
    const anchor = sheet.getRange(spec.anchor_cell);
    chart.setPosition(anchor, anchor.getOffsetRange(CHART_TILE.tileRows, CHART_TILE.tileCols));
  } else if (tileAnchor) {
    chart.setPosition(tileAnchor, tileAnchor.getOffsetRange(CHART_TILE.tileRows, CHART_TILE.tileCols));
  }
}

function mapSeriesBy(s?: string): Excel.ChartSeriesBy {
  switch ((s || '').toLowerCase()) {
    case 'rows': return 'Rows' as Excel.ChartSeriesBy;
    case 'columns': return 'Columns' as Excel.ChartSeriesBy;
    default: return 'Auto' as Excel.ChartSeriesBy;
  }
}

function mapLegendPosition(s?: string): Excel.ChartLegendPosition {
  switch ((s || '').toLowerCase()) {
    case 'top': return 'Top' as Excel.ChartLegendPosition;
    case 'bottom': return 'Bottom' as Excel.ChartLegendPosition;
    case 'left': return 'Left' as Excel.ChartLegendPosition;
    case 'right': return 'Right' as Excel.ChartLegendPosition;
    default: return 'Right' as Excel.ChartLegendPosition;
  }
}

// splitSheetQualifiedRef splits "'Employee Master'!A1:O101" into its sheet
// name and address, UNQUOTING the sheet part — Excel requires quotes
// around names with spaces, and passing the quoted form to
// worksheets.getItem() throws ItemNotFound ("requested resource doesn't
// exist" — real prod incident: a correctly-qualified pivot source failed
// to apply and left the user an empty new sheet). Doubled '' unescapes
// to a literal quote per Excel's rules.
function splitSheetQualifiedRef(ref: string): { sheetName: string; addr: string } {
  const i = ref.lastIndexOf('!');
  let sheetName = ref.slice(0, i);
  if (sheetName.startsWith("'") && sheetName.endsWith("'") && sheetName.length >= 2) {
    sheetName = sheetName.slice(1, -1).replace(/''/g, "'");
  }
  return { sheetName, addr: ref.slice(i + 1) };
}

// resolveRange accepts "A1:B10" (scoped to sheet) or "Other!A1:B10" /
// "'Other Sheet'!A1:B10" (resolved against the workbook). Chart source
// ranges commonly include a sheet prefix when data and chart live on
// different sheets.
function resolveRange(sheet: Excel.Worksheet, ref: string): Excel.Range {
  if (ref.includes('!')) {
    const { sheetName, addr } = splitSheetQualifiedRef(ref);
    const wb = sheet.context.workbook;
    const targetSheet = wb.worksheets.getItem(sheetName);
    return targetSheet.getRange(addr);
  }
  return sheet.getRange(ref);
}

// applyCreatePivot builds a PivotTable against ctx.workbook. `destSheet`
// is where the pivot is placed; source may live elsewhere.
async function applyCreatePivot(ctx: Excel.RequestContext, destSheet: string, spec: PivotSpec): Promise<void> {
  const destination = ctx.workbook.worksheets
    .getItem(destSheet)
    .getRange(spec.destination_cell);
  const source = resolvePivotSourceRange(ctx, destSheet, spec.source_range);
  const pivot = ctx.workbook.pivotTables.add(spec.name, source, destination);

  // Resolve requested field names to the pivot's REAL hierarchy names
  // (case-insensitive). The old per-field catch{} swallowed typos and
  // shipped a silently wrong pivot — a missing field now fails the change
  // loudly, and the staging-side validator catches it earlier still.
  pivot.hierarchies.load('items/name');
  await ctx.sync();
  const canonical = new Map(
    pivot.hierarchies.items.map((h) => [h.name.trim().toLowerCase(), h.name]),
  );
  const missing: string[] = [];
  const resolve = (f: string): string | undefined => {
    const hit = canonical.get((f || '').trim().toLowerCase());
    if (!hit) missing.push(f);
    return hit;
  };
  const rows = (spec.rows ?? []).map(resolve);
  const cols = (spec.columns ?? []).map(resolve);
  const filters = (spec.filters ?? []).map(resolve);
  const values = (spec.values ?? []).map((v) => ({ ...v, resolved: resolve(v.field) }));
  if (missing.length > 0) {
    // Remove the half-built pivot so a failed change leaves no debris —
    // the add was committed by the hierarchy-load sync above, so the
    // delete needs its own sync BEFORE we throw.
    const available = canonical.size > 0
      ? [...canonical.values()].join(', ')
      : '(none)';
    pivot.delete();
    await ctx.sync();
    throw new Error(
      `pivot field(s) not in source headers: ${missing.join(', ')} — available: ${available}`,
    );
  }

  for (const r of rows) {
    if (r) pivot.rowHierarchies.add(pivot.hierarchies.getItem(r));
  }
  for (const c of cols) {
    if (c) pivot.columnHierarchies.add(pivot.hierarchies.getItem(c));
  }
  for (const f of filters) {
    if (f) pivot.filterHierarchies.add(pivot.hierarchies.getItem(f));
  }
  for (const v of values) {
    if (!v.resolved) continue;
    const added = pivot.dataHierarchies.add(pivot.hierarchies.getItem(v.resolved));
    added.summarizeBy = mapAggregationFunction(v.function);
    // Analyst captions + readable numbers instead of Excel's factory
    // "Sum of X" / raw 743917.918273 (pivotDefaults.ts). PivotTable APIs
    // are all ExcelApi 1.8, so no extra gating beyond pivot support.
    added.name = friendlyHierarchyName(v.resolved, v.function);
    added.numberFormat = aggregationNumberFormat(v.function);
  }
  // Tabular layout: each row field in its own labeled column — reads like
  // a report table instead of Compact's single nested column.
  pivot.layout.layoutType = 'Tabular' as Excel.PivotLayoutType;
}

// applyDeletePivot removes a pivot table by name. Pivot tables are
// workbook-scoped, so we look up on ctx.workbook.pivotTables rather
// than a single sheet's collection. Errors bubble up to the caller's
// catch block which logs the reason on the change.
function applyDeletePivot(ctx: Excel.RequestContext, name: string): void {
  const pivot = ctx.workbook.pivotTables.getItem(name);
  pivot.delete();
}

function resolvePivotSourceRange(ctx: Excel.RequestContext, destSheet: string, ref: string): Excel.Range {
  if (ref.includes('!')) {
    const { sheetName, addr } = splitSheetQualifiedRef(ref);
    return ctx.workbook.worksheets.getItem(sheetName).getRange(addr);
  }
  // Unqualified: resolve against the pivot's OWN sheet — aligned with
  // charts and Excel-native semantics. The old apply-time-active-sheet
  // fallback was nondeterministic (the original empty-pivot incident).
  // Named ranges still work: worksheet.getRange accepts workbook-scoped
  // names.
  return ctx.workbook.worksheets.getItem(destSheet).getRange(ref);
  // No prefix: the backend qualifies cross-sheet sources at staging time,
  // so an unqualified ref should mean same-sheet — but apply can run with
  // a DIFFERENT active sheet than staging time, so this is a last resort,
  // not a contract.
  return ctx.workbook.worksheets.getActiveWorksheet().getRange(ref);
}

// mapAggregationFunction coerces our free-string function names into
// Excel.AggregationFunction. Unknown values fall back to Sum so a bad
// LLM choice doesn't fail the whole pivot.
function mapAggregationFunction(fn: string): Excel.AggregationFunction {
  switch ((fn || '').toLowerCase().replace(/[_\s-]/g, '')) {
    case 'sum': return 'Sum' as Excel.AggregationFunction;
    case 'count': return 'Count' as Excel.AggregationFunction;
    case 'countnumbers':
    case 'countnum':
    case 'numberonlycount':
      return 'CountNumbers' as Excel.AggregationFunction;
    case 'average':
    case 'avg':
    case 'mean':
      return 'Average' as Excel.AggregationFunction;
    case 'min': return 'Min' as Excel.AggregationFunction;
    case 'max': return 'Max' as Excel.AggregationFunction;
    case 'product': return 'Product' as Excel.AggregationFunction;
    case 'stdev':
    case 'standarddeviation':
      return 'StandardDeviation' as Excel.AggregationFunction;
    case 'stdevp':
    case 'standarddeviationp':
      return 'StandardDeviationP' as Excel.AggregationFunction;
    case 'var':
    case 'variance':
      return 'Variance' as Excel.AggregationFunction;
    case 'varp':
    case 'variancep':
      return 'VarianceP' as Excel.AggregationFunction;
    default:
      return 'Sum' as Excel.AggregationFunction;
  }
}

// =============================================================================
// Tier 1 — structural/layout appliers.
//
// Each helper is intentionally small and throws on bad input so the
// caller's try/catch turns it into a markFailed. We avoid silent
// fallbacks here — the user just approved a specific operation, they
// should see an error if Excel rejects it.
// =============================================================================

function applyInsertRow(sheet: Excel.Worksheet, anchor: string, count: number): void {
  const base = sheet.getRange(anchor).getEntireRow();
  const target = count > 1 ? base.getResizedRange(count - 1, 0) : base;
  target.insert('Down');
}

function applyInsertColumn(sheet: Excel.Worksheet, anchor: string, count: number): void {
  const base = sheet.getRange(anchor).getEntireColumn();
  const target = count > 1 ? base.getResizedRange(0, count - 1) : base;
  target.insert('Right');
}

function applyDeleteRow(sheet: Excel.Worksheet, anchor: string, count: number): void {
  const base = sheet.getRange(anchor).getEntireRow();
  const target = count > 1 ? base.getResizedRange(count - 1, 0) : base;
  target.delete('Up');
}

function applyDeleteColumn(sheet: Excel.Worksheet, anchor: string, count: number): void {
  const base = sheet.getRange(anchor).getEntireColumn();
  const target = count > 1 ? base.getResizedRange(0, count - 1) : base;
  target.delete('Left');
}

function applyResizeColumn(sheet: Excel.Worksheet, anchor: string, spec: ResizeSpec): void {
  const cols = sheet.getRange(anchor).getEntireColumn();
  if (spec.autofit) {
    cols.format.autofitColumns();
    return;
  }
  if (spec.width && spec.width > 0) {
    cols.format.columnWidth = spec.width;
  }
}

// applyResizeRow sets row height in points. Anchor accepts '6', '5:8', or
// any cell in the target row ('B6') — getEntireRow normalizes all three.
function applyResizeRow(sheet: Excel.Worksheet, anchor: string, spec: ResizeSpec): void {
  const rows = sheet.getRange(normalizeRowAnchor(anchor)).getEntireRow();
  if (spec.autofit) {
    rows.format.autofitRows();
    return;
  }
  if (spec.height && spec.height > 0) {
    rows.format.rowHeight = spec.height;
  }
}

// normalizeRowAnchor maps a bare row number ('6') or row span ('5:8') to an
// A1 address Office.js accepts ('6:6' / '5:8'); cell refs pass through.
function normalizeRowAnchor(anchor: string): string {
  const a = anchor.trim();
  if (/^\d+$/.test(a)) return `${a}:${a}`;
  return a;
}

function applyFreezePanes(sheet: Excel.Worksheet, spec?: FreezeSpec): void {
  // Unfreeze on zero-zero spec so users can say "remove freeze".
  if (!spec || ((spec.rows ?? 0) <= 0 && (spec.columns ?? 0) <= 0)) {
    sheet.freezePanes.unfreeze();
    return;
  }
  sheet.freezePanes.unfreeze(); // always clear before setting, to keep state clean
  if (spec.rows && spec.rows > 0) {
    sheet.freezePanes.freezeRows(spec.rows);
  }
  if (spec.columns && spec.columns > 0) {
    sheet.freezePanes.freezeColumns(spec.columns);
  }
}

// applyFindReplace uses Range.findAllOrNullObject + replaceAll so we
// don't need to round-trip cell values through Office.js manually. Scope
// is the given range, or the sheet's used range when range is empty.
function applyFindReplace(sheet: Excel.Worksheet, rangeAddr: string | undefined, spec: FindReplaceSpec): void {
  const target = rangeAddr && rangeAddr.length > 0
    ? sheet.getRange(rangeAddr)
    : sheet.getUsedRange(true);
  // replaceAll exists on Excel.Range in API 1.9+.
  const criteria: Excel.ReplaceCriteria = {
    completeMatch: !!spec.whole_cell,
    matchCase: !!spec.match_case,
  };
  // Office.js's replaceAll returns a ClientResult<number> of matches.
  target.replaceAll(spec.find, spec.replace ?? '', criteria);
}

async function applyDeleteChart(sheet: Excel.Worksheet, title: string): Promise<void> {
  // Charts are keyed by an internal name in the Office API. Users refer
  // to them by the visible title, so we load title+name, find the match,
  // and delete. The caller's outer ctx.sync() catches the delete.
  const charts = sheet.charts;
  charts.load('items/title/text,items/name');
  await sheet.context.sync();
  const hit = charts.items.find((c) => c.title?.text === title || c.name === title);
  if (!hit) {
    throw new Error(`No chart titled "${title}" on sheet "${sheet.name}"`);
  }
  hit.delete();
}

function applyRefreshPivot(ctx: Excel.RequestContext, name: string): void {
  const pivot = ctx.workbook.pivotTables.getItem(name);
  pivot.refresh();
}

// applyUpdateChart modifies an EXISTING chart in place (title / type /
// data source / legend / colors) instead of delete + recreate — the
// capability competitors have and we lacked. Only set fields change.
async function applyUpdateChart(
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  spec: ChartUpdateSpec,
): Promise<void> {
  const charts = sheet.charts;
  charts.load('items/title/text,items/name');
  await ctx.sync();
  const chart = charts.items.find((c) => c.title?.text === spec.name || c.name === spec.name);
  if (!chart) {
    throw new Error(`No chart named "${spec.name}" on sheet "${sheet.name}"`);
  }
  if (spec.new_title) chart.title.text = spec.new_title;
  if (spec.chart_type) chart.chartType = spec.chart_type as Excel.ChartType;
  if (spec.source_range) {
    // setData rebinds the chart to a new range; auto series orientation.
    // resolveRange handles sheet-qualified refs ("Data!A1:B10") — a bare
    // sheet.getRange threw on cross-sheet sources.
    chart.setData(resolveRange(sheet, spec.source_range), Excel.ChartSeriesBy.auto);
  }
  if (spec.show_legend !== undefined) chart.legend.visible = spec.show_legend;
  if (spec.legend_position) {
    if (spec.show_legend !== false) chart.legend.visible = true;
    chart.legend.position = mapLegendPosition(spec.legend_position);
  }
  if (spec.series_colors && spec.series_colors.length) {
    const series = chart.series;
    series.load('count');
    await ctx.sync();
    for (let i = 0; i < spec.series_colors.length && i < series.count; i++) {
      const hex = spec.series_colors[i];
      if (!hex) continue;
      // setSolidColor wants the hex WITHOUT the leading '#'.
      series.getItemAt(i).format.fill.setSolidColor(hex.replace(/^#/, ''));
    }
  }
}

// applyRemoveDuplicates runs Excel's native de-dupe over a range.
async function applyRemoveDuplicates(
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  rangeA1: string,
  spec?: RemoveDuplicatesSpec,
): Promise<void> {
  const range = sheet.getRange(rangeA1);
  let columns = spec?.key_columns ?? [];
  if (!columns.length) {
    // No key columns → compare all columns. Range.removeDuplicates needs
    // explicit indices, so build 0..colCount-1 from the loaded range.
    range.load('columnCount');
    await ctx.sync();
    columns = Array.from({ length: range.columnCount }, (_, i) => i);
  }
  range.removeDuplicates(columns, spec?.has_header ?? false);
}

// applyCreateTable converts a range into a native Excel Table (ListObject).
function applyCreateTable(sheet: Excel.Worksheet, rangeA1: string, spec: TableSpec): void {
  const table = sheet.tables.add(rangeA1, spec.has_headers ?? true);
  if (spec.name) {
    // Table names must be unique + syntactically valid; Office throws on
    // a bad name, which the caller's sync catches and reports.
    table.name = spec.name.replace(/\s+/g, '_');
  }
  if (spec.style) table.style = spec.style;
  if (spec.banded_rows !== undefined) table.showBandedRows = spec.banded_rows;
  if (spec.show_total_row !== undefined) table.showTotals = spec.show_total_row;
}

// applyDeleteTable removes a Table. keep_data=true converts it back to a
// plain range (data preserved); false clears the cells too.
function applyDeleteTable(ctx: Excel.RequestContext, spec: TableSpec): void {
  const table = ctx.workbook.tables.getItem(spec.name as string);
  if (spec.keep_data === false) {
    table.getRange().clear('Contents');
  }
  table.convertToRange();
}

// applyDeleteRowsWhere bulk-deletes data rows matching a column
// criterion. Scalability contract: load ONLY the criteria column's
// values (one round-trip) — never the whole range — compute matches in
// JS, then delete runs of consecutive rows BOTTOM-UP so earlier
// deletions never shift the sheet-row numbers of a still-queued run.
// Returns the number of rows deleted; zero matches is a no-op success.
async function applyDeleteRowsWhere(
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  rangeA1: string,
  spec: DeleteRowsWhereSpec,
): Promise<number> {
  const bounds = parseRangeAddress(stripSheetPrefix(rangeA1));
  if (!bounds) throw new Error(`delete_rows_where: invalid range "${rangeA1}"`);

  // Resolve the criteria column: a header-cell match in the range's
  // FIRST row wins (case-insensitive trim); otherwise treat `column` as
  // a column letter.
  const headerAddr = `${columnLetter(bounds.startCol)}${bounds.startRow}:${columnLetter(bounds.endCol)}${bounds.startRow}`;
  const headerRange = sheet.getRange(headerAddr);
  headerRange.load('values');
  await ctx.sync();
  const headers = (headerRange.values?.[0] ?? []) as unknown[];
  const wanted = String(spec.column ?? '').trim().toLowerCase();
  let colNum = 0; // 1-indexed Excel column number
  if (wanted !== '') {
    for (let i = 0; i < headers.length; i++) {
      if (String(headers[i] ?? '').trim().toLowerCase() === wanted) {
        colNum = bounds.startCol + i;
        break;
      }
    }
  }
  if (colNum === 0) {
    if (!/^[A-Za-z]{1,3}$/.test(spec.column ?? '')) {
      throw new Error(`delete_rows_where: column "${spec.column}" is neither a header in ${rangeA1} nor a column letter`);
    }
    colNum = colLetterToIndex(spec.column.toUpperCase());
  }

  // Data rows exclude the header row (sheet row bounds.startRow).
  const dataStart = bounds.startRow + 1;
  if (bounds.endRow < dataStart) return 0;

  const colLet = columnLetter(colNum);
  const colRange = sheet.getRange(`${colLet}${dataStart}:${colLet}${bounds.endRow}`);
  colRange.load('values');
  await ctx.sync();
  const values = (colRange.values ?? []) as unknown[][];

  // Matching sheet-row numbers (1-based), ascending — values[i] is the
  // cell at sheet row dataStart + i.
  const matcher = buildRowMatcher(spec);
  const matchRows: number[] = [];
  for (let i = 0; i < values.length; i++) {
    if (matcher(values[i]?.[0])) matchRows.push(dataStart + i);
  }
  if (matchRows.length === 0) return 0;

  // Group consecutive rows into runs so wiping 60k contiguous rows is a
  // handful of delete ops, not 60k.
  const runs: Array<{ start: number; end: number }> = [];
  for (const row of matchRows) {
    const last = runs[runs.length - 1];
    if (last && row === last.end + 1) last.end = row;
    else runs.push({ start: row, end: row });
  }

  // Delete BOTTOM-UP (highest run first) with a periodic sync so a
  // fragmented delete (hundreds of runs) doesn't build one giant batch.
  let deletesSinceSync = 0;
  for (let i = runs.length - 1; i >= 0; i--) {
    sheet.getRange(`${runs[i].start}:${runs[i].end}`).delete(Excel.DeleteShiftDirection.up);
    if (++deletesSinceSync >= 50) {
      await ctx.sync();
      deletesSinceSync = 0;
    }
  }
  await ctx.sync();
  return matchRows.length;
}

// buildRowMatcher compiles a DeleteRowsWhereSpec into a cell predicate.
// Text ops compare String() forms, case-insensitive unless match_case;
// numeric ops (gt/lt/between) skip non-numeric cells entirely; 'empty'
// matches "" and null. Unknown ops throw → the change fails with reason.
function buildRowMatcher(spec: DeleteRowsWhereSpec): (v: unknown) => boolean {
  const op = spec.op;
  const caseSensitive = spec.match_case === true;
  const norm = (s: string) => (caseSensitive ? s : s.toLowerCase());
  const text = norm(String(spec.value ?? ''));
  const num1 = Number(spec.value);
  const num2 = Number(spec.value2);
  switch (op) {
    case 'empty':
      return (v) => v == null || v === '';
    case 'equals':
      return (v) => norm(String(v ?? '')) === text;
    case 'not_equals':
      return (v) => norm(String(v ?? '')) !== text;
    case 'contains':
      return (v) => norm(String(v ?? '')).includes(text);
    case 'gt':
    case 'lt':
    case 'between':
      return (v) => {
        if (v == null || v === '') return false; // blanks never match numeric ops
        const n = Number(v);
        if (!Number.isFinite(n)) return false;   // skip non-numeric cells
        if (op === 'gt') return Number.isFinite(num1) && n > num1;
        if (op === 'lt') return Number.isFinite(num1) && n < num1;
        return Number.isFinite(num1) && Number.isFinite(num2) && n >= num1 && n <= num2;
      };
    default:
      throw new Error(`delete_rows_where: unsupported op "${op}"`);
  }
}

// applyFillFormula writes the seed formula into the FIRST cell of the
// range, then extends it across the full range via Excel's native
// autofill — relative references adjust exactly like a manual fill-down,
// and Excel does the expansion (no 100k-row grid built in JS). autoFill
// is ExcelApi 1.9; on older hosts we fall back to a client-side
// expansion for small ranges, else fail the change with a clear reason.
const FILL_FORMULA_FALLBACK_MAX_ROWS = 5000;

async function applyFillFormula(
  sheet: Excel.Worksheet,
  rangeA1: string,
  formula: string,
): Promise<void> {
  const ctx = sheet.context;
  const fullRange = sheet.getRange(rangeA1);
  const firstCell = fullRange.getCell(0, 0);

  // Flush anything already queued on this context FIRST. The autoFill
  // try/catch below must only ever swallow ITS OWN failure — inside a
  // chunked apply, an earlier change's queued ops would otherwise flush
  // (and fail) at the autoFill sync and be silently eaten here instead
  // of propagating for the chunk replay to attribute.
  await ctx.sync();

  if (Office.context.requirements.isSetSupported('ExcelApi', '1.9')) {
    try {
      firstCell.formulas = [[formula]];
      // autoFill(destination, type): destination must CONTAIN the source
      // cell — the full target range does, by construction.
      firstCell.autoFill(fullRange, Excel.AutoFillType.fillDefault);
      await ctx.sync();
      return;
    } catch {
      // autoFill rejected at sync (host quirk / unsupported range shape).
      // The failed sync also discarded the queued seed write, so the
      // fallback below starts from a clean slate.
    }
  }

  // Fallback: expand client-side by bumping relative row references —
  // what a fill-down does for a column target. Bounded so we never
  // build a six-figure-row grid in JS.
  const bounds = parseRangeAddress(stripSheetPrefix(rangeA1));
  if (!bounds) throw new Error(`fill_formula: invalid range "${rangeA1}"`);
  const rowCount = bounds.endRow - bounds.startRow + 1;
  const colCount = bounds.endCol - bounds.startCol + 1;
  if (rowCount > FILL_FORMULA_FALLBACK_MAX_ROWS) {
    throw new Error('autofill unsupported on this Excel version');
  }
  const grid: string[][] = [];
  for (let r = 0; r < rowCount; r++) {
    grid.push(new Array<string>(colCount).fill(bumpRelativeRows(formula, r)));
  }
  fullRange.formulas = grid;
  await ctx.sync();
}

// bumpRelativeRows adds `offset` to every RELATIVE row number in an
// A1-style reference (D2 → D3; D$2 and $D$2 stay pinned). Deliberately a
// simple regex: the look-ahead rejects partial digit matches and
// function names like LOG10(, and sheet-qualified prefixes (Sheet1!)
// are excluded via the trailing !/' guard.
function bumpRelativeRows(formula: string, offset: number): string {
  if (offset === 0) return formula;
  return formula.replace(
    /(\$?[A-Za-z]{1,3})(\$?)(\d+)(?![\d(!'])/g,
    (whole, colPart: string, rowAbs: string, rowNum: string) =>
      rowAbs === '$' ? whole : `${colPart}${parseInt(rowNum, 10) + offset}`,
  );
}

// =============================================================================
// Tier 2 — shape + reference appliers.
// =============================================================================

// applyFilterRange attaches an autofilter to the range and sets the
// column criteria. Kind is a free string — we translate into Office.js's
// Excel.FilterCriteria on a best-effort basis; unknown kinds throw.
//
// Production-grade behavior:
//
//   1. RESET before apply. autoFilter is sheet-scoped — once enabled,
//      every column filter accumulates. If a prior turn filtered
//      Status=Active and the new turn says "filter Skill=Carpenter",
//      Office.js does NOT auto-clear the Status filter; the user sees
//      an AND of both, which looks broken. We `remove()` the autofilter
//      first (safe no-op when none exists) so each apply starts clean.
//
//   2. RE-ANCHOR. `remove()` strips the existing range binding so the
//      new `apply(range, ...)` re-anchors to whatever the model passed,
//      not a stale range from the previous turn.
//
//   3. RANGE-RELATIVE columnIndex per Office.js docs ("zero-based column
//      index" — interpreted within the range parameter, matching VBA
//      AutoFilter `Field` precedent and the cookbook sample at
//      learn.microsoft.com/excel-add-ins-tables). Backend schema also
//      declares the column as "0-indexed within the range" — applier
//      contract matches the backend contract. (Earlier conversion to
//      worksheet-absolute was incorrect; reverted.)
function applyFilterRange(sheet: Excel.Worksheet, rangeAddr: string, spec: FilterSpec): void {
  const af = sheet.autoFilter;
  // Step 1: clean slate. remove() drops any existing autofilter on the
  // sheet; no-op when none is set. Wrapped in try/catch defensively
  // because Office.js can reject remove() during certain transient
  // states (e.g. sheet has filtered tables that own the filter).
  try {
    af.remove();
  } catch {
    // Fall back to clearCriteria when remove() is unavailable. Both
    // outcomes still get us a clean filter state for the new apply.
    try { af.clearCriteria(); } catch { /* nothing more we can do */ }
  }

  // Step 2: apply the new filter. range is re-anchored automatically.
  const range = sheet.getRange(rangeAddr);
  const criteria = buildFilterCriteria(spec);
  af.apply(range, spec.column ?? 0, criteria);
}

function buildFilterCriteria(spec: FilterSpec): Excel.FilterCriteria {
  const kind = (spec.kind || '').toLowerCase();
  switch (kind) {
    case 'values':
      return {
        filterOn: 'Values' as Excel.FilterOn,
        values: spec.values ?? [],
      };
    case 'contains':
      return {
        filterOn: 'Custom' as Excel.FilterOn,
        criterion1: '*' + (spec.values?.[0] ?? '') + '*',
      };
    case 'greater_than':
      return {
        filterOn: 'Custom' as Excel.FilterOn,
        criterion1: '>' + (spec.values?.[0] ?? ''),
      };
    case 'less_than':
      return {
        filterOn: 'Custom' as Excel.FilterOn,
        criterion1: '<' + (spec.values?.[0] ?? ''),
      };
    case 'between':
      return {
        filterOn: 'Custom' as Excel.FilterOn,
        criterion1: '>=' + (spec.values?.[0] ?? ''),
        criterion2: '<=' + (spec.values?.[1] ?? ''),
        operator: 'And' as Excel.FilterOperator,
      };
    case 'top':
      return {
        filterOn: (spec.percent ? 'TopPercent' : 'TopItems') as Excel.FilterOn,
        criterion1: String(spec.rank ?? 10),
      };
    case 'bottom':
      return {
        filterOn: (spec.percent ? 'BottomPercent' : 'BottomItems') as Excel.FilterOn,
        criterion1: String(spec.rank ?? 10),
      };
    case 'above_average':
      return { filterOn: 'Dynamic' as Excel.FilterOn, dynamicCriteria: 'AboveAverage' as Excel.DynamicFilterCriteria };
    case 'below_average':
      return { filterOn: 'Dynamic' as Excel.FilterOn, dynamicCriteria: 'BelowAverage' as Excel.DynamicFilterCriteria };
    default:
      throw new Error(`unsupported filter kind: ${spec.kind}`);
  }
}

function applyClearFilter(sheet: Excel.Worksheet): void {
  sheet.autoFilter.remove();
}

// applyTransposeRange reads the source range's values + formulas and
// writes them into destination with rows and columns swapped. We use
// copyFrom with transpose=true when available for a single round-trip.
async function applyTransposeRange(sheet: Excel.Worksheet, sourceAddr: string, destAddr: string): Promise<void> {
  const src = sheet.getRange(sourceAddr);
  src.load(['rowCount', 'columnCount']);
  await sheet.context.sync();
  const dest = sheet.getRange(destAddr).getResizedRange(src.columnCount - 1, src.rowCount - 1);
  // copyFrom with copyType=All preserves values + formats + formulas.
  // The third arg asks for a transpose.
  dest.copyFrom(src, 'All' as Excel.RangeCopyType, false, true);
}

// applyTextToColumns splits a 1-column range by delimiter using Excel's
// built-in "Convert text to columns" behavior, exposed via Range.convertTextToColumns
// on newer API versions. We defensively fall back to a manual split + write
// when the native call isn't available.
async function applyTextToColumns(sheet: Excel.Worksheet, rangeAddr: string, spec: TextToColumnsSpec): Promise<void> {
  const range = sheet.getRange(rangeAddr);
  const rangeAny = range as unknown as {
    convertTextToColumns?: (args: unknown) => void;
  };
  if (typeof rangeAny.convertTextToColumns === 'function') {
    // Newer API path — Office handles the expansion into adjacent cols.
    rangeAny.convertTextToColumns!({
      delimiter: spec.delimiter ?? '',
      treatConsecutiveDelimitersAsOne: !!spec.treat_consecutive_as_one,
      fixedWidth: (spec.fixed_widths?.length ?? 0) > 0,
      fixedWidthSplits: spec.fixed_widths ?? [],
    });
    return;
  }
  // Fallback: read values, split in JS, write to expanded range.
  range.load(['values', 'address', 'rowCount']);
  await sheet.context.sync();
  const delimiter = spec.delimiter || '';
  if (!delimiter) {
    throw new Error('text_to_columns fallback requires a delimiter; fixed widths not supported in this runtime');
  }
  const rows = range.values;
  const split: unknown[][] = rows.map((row) => {
    const raw = String(row[0] ?? '');
    const parts = spec.treat_consecutive_as_one
      ? raw.split(delimiter).filter((p) => p.length > 0)
      : raw.split(delimiter);
    return parts as unknown[];
  });
  const maxCols = split.reduce((m, r) => Math.max(m, r.length), 1);
  const padded = split.map((r) => {
    const out = new Array(maxCols).fill(null);
    for (let i = 0; i < r.length; i++) out[i] = r[i];
    return out;
  });
  const expanded = range.getResizedRange(0, maxCols - 1);
  expanded.values = padded;
}

// applyCreateNamedRange installs a name either at the workbook level or
// on a specific sheet. The applier trusts the refers-to string — Excel
// validates at apply time (our backend validator already enforces the
// identifier charset).
function applyCreateNamedRange(ctx: Excel.RequestContext, spec: NamedRangeSpec): void {
  const refers = normalizeNamedRefers(spec.refers ?? '');
  if (!spec.scope || spec.scope.toLowerCase() === 'workbook') {
    ctx.workbook.names.add(spec.name, refers, spec.comment || '');
    return;
  }
  const sheet = ctx.workbook.worksheets.getItem(spec.scope);
  sheet.names.add(spec.name, refers, spec.comment || '');
}

function applyDeleteNamedRange(ctx: Excel.RequestContext, spec: NamedRangeSpec): void {
  if (!spec.scope || spec.scope.toLowerCase() === 'workbook') {
    const n = ctx.workbook.names.getItem(spec.name);
    n.delete();
    return;
  }
  const sheet = ctx.workbook.worksheets.getItem(spec.scope);
  const n = sheet.names.getItem(spec.name);
  n.delete();
}

// normalizeNamedRefers accepts "Sheet1!A1:B10" or "=Sheet1!$A$1:$B$10"
// and returns the leading-"=" form that names.add expects.
function normalizeNamedRefers(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return trimmed;
  return trimmed.startsWith('=') ? trimmed : '=' + trimmed;
}

// applyDataValidation configures Excel.DataValidation for the supplied
// range. Unknown types throw. We translate operator + values into
// Excel's validation rule shapes on a best-effort basis.
function applyDataValidation(range: Excel.Range, spec: DataValidationSpec): void {
  const dv = range.dataValidation;
  dv.clear(); // remove any prior rule before applying the new one

  const type = (spec.type || '').toLowerCase();
  const op = mapValidationOperator(spec.operator);
  const rule: Excel.DataValidationRule = {};

  switch (type) {
    case 'list': {
      const source = spec.values && spec.values.length > 0
        ? spec.values.join(',')
        : (spec.formula ?? '');
      rule.list = {
        inCellDropDown: spec.show_dropdown !== false,
        source,
      };
      break;
    }
    case 'whole_number':
      rule.wholeNumber = buildNumericRule(op, spec.values);
      break;
    case 'decimal':
      rule.decimal = buildNumericRule(op, spec.values);
      break;
    case 'date':
      rule.date = buildDateRule(op, spec.values) as unknown as Excel.DateTimeDataValidation;
      break;
    case 'text_length':
      rule.textLength = buildNumericRule(op, spec.values);
      break;
    case 'custom':
      rule.custom = { formula: spec.formula ?? '' };
      break;
    default:
      throw new Error(`unsupported data-validation type: ${spec.type}`);
  }
  dv.rule = rule;

  if (spec.prompt || spec.prompt_title) {
    dv.prompt = {
      showPrompt: true,
      title: spec.prompt_title ?? '',
      message: spec.prompt ?? '',
    };
  }
  if (spec.error || spec.error_title) {
    dv.errorAlert = {
      showAlert: true,
      style: mapValidationErrorStyle(spec.error_style),
      title: spec.error_title ?? 'Invalid value',
      message: spec.error ?? 'This entry violates the data validation rule.',
    };
  }
  if (spec.ignore_blank !== undefined) {
    dv.ignoreBlanks = spec.ignore_blank;
  }
}

function mapValidationOperator(op?: string): Excel.DataValidationOperator {
  switch ((op || '').toLowerCase()) {
    case 'not_between': return 'NotBetween' as Excel.DataValidationOperator;
    case 'equal_to': return 'EqualTo' as Excel.DataValidationOperator;
    case 'not_equal_to': return 'NotEqualTo' as Excel.DataValidationOperator;
    case 'greater_than': return 'GreaterThan' as Excel.DataValidationOperator;
    case 'less_than': return 'LessThan' as Excel.DataValidationOperator;
    case 'greater_than_or_equal_to': return 'GreaterThanOrEqualTo' as Excel.DataValidationOperator;
    case 'less_than_or_equal_to': return 'LessThanOrEqualTo' as Excel.DataValidationOperator;
    default: return 'Between' as Excel.DataValidationOperator;
  }
}

function mapValidationErrorStyle(s?: string): Excel.DataValidationAlertStyle {
  switch ((s || '').toLowerCase()) {
    case 'warning': return 'Warning' as Excel.DataValidationAlertStyle;
    case 'information': return 'Information' as Excel.DataValidationAlertStyle;
    default: return 'Stop' as Excel.DataValidationAlertStyle;
  }
}

function buildNumericRule(op: Excel.DataValidationOperator, values?: string[]): Excel.BasicDataValidation {
  const formula1 = values?.[0] ?? '0';
  const formula2 = values?.[1] ?? '';
  return { operator: op, formula1, formula2 };
}

function buildDateRule(op: Excel.DataValidationOperator, values?: string[]): Excel.BasicDataValidation {
  // Dates ride the same shape — callers should supply ISO-like strings
  // or Excel serial numbers; Excel accepts both.
  return buildNumericRule(op, values);
}

// =============================================================================
// Tier 3 — protection, comments.
// =============================================================================

function applyProtectSheet(sheet: Excel.Worksheet, spec?: ProtectSpec): void {
  const options: Excel.WorksheetProtectionOptions = {};
  if (spec?.allow_edit_objects !== undefined) options.allowEditObjects = spec.allow_edit_objects;
  if (spec?.allow_format_cells !== undefined) options.allowFormatCells = spec.allow_format_cells;
  if (spec?.allow_insert_rows !== undefined) options.allowInsertRows = spec.allow_insert_rows;
  if (spec?.allow_insert_cols !== undefined) options.allowInsertColumns = spec.allow_insert_cols;
  if (spec?.allow_delete_rows !== undefined) options.allowDeleteRows = spec.allow_delete_rows;
  if (spec?.allow_delete_cols !== undefined) options.allowDeleteColumns = spec.allow_delete_cols;
  if (spec?.allow_sort !== undefined) options.allowSort = spec.allow_sort;
  if (spec?.allow_filter !== undefined) options.allowAutoFilter = spec.allow_filter;
  if (spec?.allow_pivot_tables !== undefined) options.allowPivotTables = spec.allow_pivot_tables;
  sheet.protection.protect(options, spec?.password);
}

function applyAddComment(ctx: Excel.RequestContext, sheetName: string, cell: string, spec: CommentSpec): void {
  const anchor = sheetName + '!' + cell;
  const content = spec.text ?? '';
  if (spec.mentions && spec.mentions.length > 0) {
    // Office.js threaded comments accept a content with @-mention markup;
    // we let the LLM do that formatting in spec.text. Mentions list is
    // advisory for downstream features — not wired here to keep scope
    // tight.
    ctx.workbook.comments.add(anchor, content);
    return;
  }
  ctx.workbook.comments.add(anchor, content);
}

async function applyDeleteComment(ctx: Excel.RequestContext, sheetName: string, cell: string): Promise<void> {
  // Office.js doesn't expose a direct "get comment by cell". We load all
  // comments on the sheet, find the one anchored to the given cell, and
  // delete it.
  const sheet = ctx.workbook.worksheets.getItem(sheetName);
  const comments = sheet.comments;
  comments.load('items/content,items/getLocation');
  await ctx.sync();
  // Resolve each comment's anchor address; the Office API doesn't expose
  // this cheaply so we just load addresses one-by-one.
  for (const c of comments.items) {
    const loc = c.getLocation();
    loc.load('address');
    await ctx.sync();
    const addr = (loc.address || '').split('!').pop();
    if (addr && addr.toUpperCase() === cell.toUpperCase()) {
      c.delete();
      return;
    }
  }
  throw new Error(`No comment found on ${sheetName}!${cell}`);
}


// expandFillSeries materialises a FillSeriesSpec into a 2-D values
// grid that fits the target range. Server sends ~4 numbers, we expand
// here — saves the LLM round-trip from sending every single value.
//
// Mode behaviours:
//   linear   — start + step * i (numeric)
//   growth   — start * step^i (geometric, numeric)
//   date     — start date + i days (or whatever step says: day/weekday/month/year)
//   weekday  — start date + i weekdays (skips Sat/Sun)
//   month    — start date + i months
//   year     — start date + i years
//   autofill — Excel-style pattern from seeds (we handle a few common
//              patterns; complex pattern detection requires Excel's API
//              and would need a ctx.sync inside — skipped for now).
function expandFillSeries(
  spec: import('@/types/chat').FillSeriesSpec,
  rows: number,
  cols: number,
): unknown[][] | null {
  const total = rows * cols;
  if (total <= 0) return null;
  const values: unknown[] = [];

  switch (spec.mode) {
    case 'linear': {
      const start = Number(spec.start);
      const step = spec.step === undefined ? 1 : Number(spec.step);
      if (Number.isNaN(start) || Number.isNaN(step)) return null;
      const stop = spec.stop === undefined ? null : Number(spec.stop);
      for (let i = 0; i < total; i++) {
        const v = start + step * i;
        if (stop !== null && ((step > 0 && v > stop) || (step < 0 && v < stop))) {
          values.push(null);
        } else {
          values.push(v);
        }
      }
      break;
    }
    case 'growth': {
      const start = Number(spec.start);
      const step = spec.step === undefined ? 2 : Number(spec.step);
      if (Number.isNaN(start) || Number.isNaN(step)) return null;
      for (let i = 0; i < total; i++) values.push(start * Math.pow(step, i));
      break;
    }
    case 'date':
    case 'weekday':
    case 'month':
    case 'year': {
      const startDate = parseISODate(String(spec.start ?? ''));
      if (!startDate) return null;
      const stepKind =
        spec.mode === 'weekday' ? 'weekday' :
        spec.mode === 'month'   ? 'month'   :
        spec.mode === 'year'    ? 'year'    :
        (typeof spec.step === 'string' ? spec.step : 'day');
      for (let i = 0; i < total; i++) {
        values.push(addDateStep(startDate, i, stepKind));
      }
      break;
    }
    case 'autofill': {
      // Limited autofill support: numeric-arithmetic and weekday-name
      // patterns from 2-3 seeds. Anything more sophisticated would need
      // Excel's native autofill (range.autoFill) which requires a
      // running Excel.run context — defer to future enhancement.
      const seeds = spec.seeds ?? [];
      if (seeds.length >= 2 && seeds.every((v) => typeof v === 'number')) {
        const a = Number(seeds[0]);
        const b = Number(seeds[1]);
        const step = b - a;
        for (let i = 0; i < total; i++) values.push(a + step * i);
      } else if (seeds.length > 0) {
        // String pattern — repeat seeds verbatim.
        for (let i = 0; i < total; i++) values.push(seeds[i % seeds.length]);
      } else {
        return null;
      }
      break;
    }
    default:
      return null;
  }

  // Reshape flat array into [rows][cols]. Row-major fill order matches
  // user expectation for "fill A1:C5" (A1, B1, C1, A2, B2, ..., C5).
  const grid: unknown[][] = [];
  for (let r = 0; r < rows; r++) {
    grid.push(values.slice(r * cols, (r + 1) * cols));
  }
  return grid;
}

// parseISODate accepts "2026-04-23", "2026-04-23T10:00", or a plain
// date string Excel/JS Date can swallow. Returns null on failure.
function parseISODate(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// addDateStep returns the date i steps after start. step is one of
// "day" | "weekday" | "month" | "year". Output is ISO "yyyy-mm-dd"
// — Excel auto-formats as date when the column is dates.
function addDateStep(start: Date, i: number, step: string): string {
  const d = new Date(start);
  switch (step) {
    case 'weekday': {
      // Add i weekdays (skip Sat/Sun).
      let added = 0;
      while (added < i) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) added++;
      }
      break;
    }
    case 'month':
      d.setMonth(d.getMonth() + i);
      break;
    case 'year':
      d.setFullYear(d.getFullYear() + i);
      break;
    case 'day':
    default:
      d.setDate(d.getDate() + i);
      break;
  }
  // ISO yyyy-mm-dd (no time component) — Excel renders as date.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// =============================================================================
// Tier 4 — What-If Analysis appliers
// =============================================================================
// All three tools share the same shape:
//   1. Save the original value(s) of the input cell(s) so we can
//      restore on failure or after capturing outputs.
//   2. Run the iterative / multi-pass write+sync+read loop.
//   3. Either keep the final state (goal_seek success) or restore
//      originals and write a result table (sensitivity / scenarios).
// All execute inside the SAME Excel.run() block as the rest of the
// applier — multiple ctx.sync() calls are batched cheaply by Office.js.

/** applyGoalSeek runs a secant-method search to find the input value
 *  of changing_cell that drives target_cell to target_value.
 *  Restores the original input on failure; leaves converged value on success. */
async function applyGoalSeek(
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  spec: GoalSeekSpec,
): Promise<void> {
  const tol = spec.tolerance && spec.tolerance > 0 ? spec.tolerance : 0.001;
  const maxIter = spec.max_iterations && spec.max_iterations > 0 ? spec.max_iterations : 100;
  const initialStep = spec.initial_step && spec.initial_step !== 0 ? spec.initial_step : 1.0;

  const inputRange = sheet.getRange(spec.changing_cell);
  const targetRange = sheet.getRange(spec.target_cell);

  // Read originals first so we can restore on any failure path.
  inputRange.load('values');
  targetRange.load('values');
  await ctx.sync();
  const originalInput = Number(inputRange.values?.[0]?.[0] ?? 0);
  if (!Number.isFinite(originalInput)) {
    throw new Error(`Goal Seek: changing_cell ${spec.changing_cell} is not numeric (got ${inputRange.values?.[0]?.[0]})`);
  }

  // Helper: probe one value, return f(x) = target_actual - target_value.
  const probe = async (x: number): Promise<number> => {
    inputRange.values = [[x]];
    targetRange.load('values');
    await ctx.sync();
    const v = targetRange.values?.[0]?.[0];
    const num = Number(v);
    if (!Number.isFinite(num)) {
      throw new Error(`Goal Seek: target_cell ${spec.target_cell} returned non-numeric (${v}) for input=${x}`);
    }
    return num - spec.target_value;
  };

  // Seed two probes for secant method.
  let x0 = originalInput;
  let f0 = await probe(x0);
  if (Math.abs(f0) < tol) {
    // Already at target — leave as-is, no iteration needed.
    return;
  }
  // Step away from x0: an explicitly-provided positive initial_step
  // wins; otherwise 1% of x0, falling back to initialStep when x0 ≈ 0.
  const explicitStep = typeof spec.initial_step === 'number'
    && Number.isFinite(spec.initial_step) && spec.initial_step > 0
    ? spec.initial_step
    : undefined;
  const step = explicitStep
    ?? (Math.abs(originalInput) > 1e-9 ? originalInput * 0.01 : initialStep);
  let x1 = x0 + (step !== 0 ? step : initialStep);
  let f1 = await probe(x1);

  let converged = false;
  for (let i = 0; i < maxIter; i++) {
    if (Math.abs(f1) < tol) {
      converged = true;
      break;
    }
    const denom = f1 - f0;
    if (Math.abs(denom) < 1e-12) {
      // Stuck — gradient is zero or function flat. Cannot proceed.
      break;
    }
    const x2 = x1 - f1 * (x1 - x0) / denom;
    if (!Number.isFinite(x2)) {
      break;
    }
    const f2 = await probe(x2);
    x0 = x1; f0 = f1;
    x1 = x2; f1 = f2;
  }

  if (!converged) {
    // Restore original and surface a clear error so the user (and model
    // recovery hint) can choose: pick a different changing_cell, widen
    // tolerance, or accept the closest value.
    inputRange.values = [[originalInput]];
    await ctx.sync();
    throw new Error(`Goal Seek did not converge within ${maxIter} iterations (closest residual ${f1.toFixed(6)}). Original value restored.`);
  }
  // Success: x1 is already in the input cell from the last probe.
  // No further write needed.
}

/** applySensitivityTable runs a 1-var or 2-var Data Table:
 *  for each row_value (and optional col_value) write the input(s),
 *  read the recomputed formula, then build the result grid and
 *  write it at output_anchor. Inputs are restored after iteration. */
async function applySensitivityTable(
  ctx: Excel.RequestContext,
  inputSheet: Excel.Worksheet,
  spec: SensitivityTableSpec,
): Promise<void> {
  const isTwoVar = !!(spec.col_input_cell && spec.col_values && spec.col_values.length > 0);
  const includeLabels = spec.include_labels !== false; // default true

  const formulaRange = inputSheet.getRange(spec.formula_cell);
  const rowInputRange = inputSheet.getRange(spec.row_input_cell);
  const colInputRange = isTwoVar ? inputSheet.getRange(spec.col_input_cell!) : null;

  // Snapshot originals for restoration.
  rowInputRange.load('values');
  if (colInputRange) colInputRange.load('values');
  await ctx.sync();
  const origRow = rowInputRange.values?.[0]?.[0];
  const origCol = colInputRange ? colInputRange.values?.[0]?.[0] : undefined;

  // Build the output grid as 2D array, then ONE write at the end.
  const grid: unknown[][] = [];

  try {
    if (!isTwoVar) {
      // 1-variable: 2-column table [input_value | formula_result]
      if (includeLabels) {
        grid.push([spec.row_input_cell, spec.formula_cell]);
      }
      for (const rv of spec.row_values) {
        rowInputRange.values = [[rv]];
        formulaRange.load('values');
        await ctx.sync();
        grid.push([rv, formulaRange.values?.[0]?.[0]]);
      }
    } else {
      // 2-variable: (R+1) x (C+1) grid with axes as labels.
      const rowVals = spec.row_values;
      const colVals = spec.col_values!;
      if (includeLabels) {
        // Header row: corner-label (formula_cell ref) then col_values
        const header: unknown[] = [`${spec.row_input_cell}\\${spec.col_input_cell}`];
        header.push(...colVals);
        grid.push(header);
      }
      for (const rv of rowVals) {
        rowInputRange.values = [[rv]];
        const rowResult: unknown[] = includeLabels ? [rv] : [];
        for (const cv of colVals) {
          colInputRange!.values = [[cv]];
          formulaRange.load('values');
          await ctx.sync();
          rowResult.push(formulaRange.values?.[0]?.[0]);
        }
        grid.push(rowResult);
      }
    }
  } finally {
    // Always restore inputs, even if the loop threw partway.
    rowInputRange.values = [[origRow]];
    if (colInputRange) colInputRange.values = [[origCol]];
    await ctx.sync();
  }

  // Write the result table at output_anchor on the chosen sheet.
  const outSheet = spec.output_sheet
    ? ctx.workbook.worksheets.getItem(spec.output_sheet)
    : inputSheet;
  const rows = grid.length;
  const cols = grid.reduce((m, r) => Math.max(m, r.length), 0);
  // Pad ragged rows so Excel accepts the 2D array.
  for (const r of grid) {
    while (r.length < cols) r.push('');
  }
  const anchor = outSheet.getRange(spec.output_anchor);
  anchor.load('rowIndex,columnIndex');
  await ctx.sync();
  const fullRange = outSheet.getRangeByIndexes(anchor.rowIndex, anchor.columnIndex, rows, cols);
  fullRange.values = grid;
  await ctx.sync();
}

/** applyScenarios runs each named input set, captures output_cells,
 *  restores originals, and writes a comparison table at output_anchor.
 *  Output layout: header [Scenario, output_label_1, output_label_2, ...]
 *  followed by one row per scenario. */
async function applyScenarios(
  ctx: Excel.RequestContext,
  inputSheet: Excel.Worksheet,
  spec: ScenariosSpec,
): Promise<void> {
  // Collect the union of all input cells across scenarios so we know
  // which cells to snapshot/restore. Different scenarios may touch
  // different inputs — we save them all to be safe.
  const allInputCells = new Set<string>();
  for (const sc of spec.scenarios) {
    for (const cell of Object.keys(sc.inputs)) {
      allInputCells.add(cell.toUpperCase());
    }
  }
  const inputAddrs = [...allInputCells];
  const inputRanges = inputAddrs.map((addr) => inputSheet.getRange(addr));
  inputRanges.forEach((r) => r.load('values'));

  const outputAddrs = spec.output_cells.map((c) => c.toUpperCase());
  const outputRanges = outputAddrs.map((addr) => inputSheet.getRange(addr));
  await ctx.sync();

  // Capture originals.
  const originals: Record<string, unknown> = {};
  inputAddrs.forEach((addr, i) => {
    originals[addr] = inputRanges[i].values?.[0]?.[0];
  });

  // Run each scenario, collect outputs.
  const labels = spec.output_labels && spec.output_labels.length === outputAddrs.length
    ? spec.output_labels
    : outputAddrs;
  const grid: unknown[][] = [['Scenario', ...labels]];
  try {
    for (const sc of spec.scenarios) {
      // Apply this scenario's inputs.
      for (const [cellRaw, val] of Object.entries(sc.inputs)) {
        const cell = cellRaw.toUpperCase();
        inputSheet.getRange(cell).values = [[val as Excel.CellValue | string | number | boolean | null]];
      }
      // Reads need a separate sync after writes for recalc to materialise.
      outputRanges.forEach((r) => r.load('values'));
      await ctx.sync();
      const row: unknown[] = [sc.name];
      for (const r of outputRanges) {
        row.push(r.values?.[0]?.[0]);
      }
      grid.push(row);
    }
  } finally {
    // Restore originals on every input cell we touched.
    inputAddrs.forEach((addr, i) => {
      inputRanges[i].values = [[originals[addr] as Excel.CellValue | string | number | boolean | null]];
    });
    await ctx.sync();
  }

  // Write comparison table at output_anchor.
  const outSheet = spec.output_sheet
    ? ctx.workbook.worksheets.getItem(spec.output_sheet)
    : inputSheet;
  const anchor = outSheet.getRange(spec.output_anchor);
  anchor.load('rowIndex,columnIndex');
  await ctx.sync();
  const rows = grid.length;
  const cols = grid[0].length;
  // Pad ragged rows defensively.
  for (const r of grid) {
    while (r.length < cols) r.push('');
  }
  const fullRange = outSheet.getRangeByIndexes(anchor.rowIndex, anchor.columnIndex, rows, cols);
  fullRange.values = grid;
  await ctx.sync();
}
